import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import Page from '../../atoms/Page';
import useUser from '../../utils/hooks/useUser';
import TabSection from '../../molecules/TabSection';
import UnsavedChangesBar from '../../molecules/UnsavedChangesBar';
import './BarnSettingsTabV2.scss';
import { CalibrationStyle, CalibrationStyleToString } from './enum';
import { FieldArray, Form, Formik } from 'formik';
import TextInputField from '../../atoms/TextInput/TextInputField';
import TextInput from '../../atoms/TextInput/TextInput';
import Heading from '../../atoms/Heading';
import Button, { Colors } from '../../atoms/Button';
import { ComboboxMultiField } from '../../atoms/ComboboxMulti';
import { ComboboxField } from '../../atoms/Combobox';
import NumberInputField from '../../atoms/NumberInput/NumberInputField';
import { toast } from 'react-toastify';
import { Privilege, useCan } from '@feedflo/access-control';
import ConfirmationDialog from '../../organisms/ConfirmationDialog';
import InviteButton from '../../molecules/InviteModal/InviteButton';
import FeedFloToolTip from '../../atoms/FeedFloToolTip/FeedFloToolTip';
import { InfoIconSVG, PencilIconSVG } from '../../atoms/Icons';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';

import {
  convertGramsToLargeUnits,
  convertGramsToSmallUnits,
  convertLargeUnitsToGrams,
  weightLargeUnitLabel,
  weightSmallUnitLabel,
} from '../../utils/unitConversion';
import { alertHoursToThresholdSeconds, alertThresholdSecondsToHours } from '../../utils/tierNotificationSettings';
import { NO_NOTIFICATIONS_TIER_NAME } from '../../utils/constants';
import { resolveDefaultTierId } from '../../utils/notificationTierAssignment';
import SelectField from '../../atoms/Select/SelectField';
import EditFeederModalTrigger from './EditFeederModalTrigger';
import {
  DELETE_USER_BARN_TIER_ASSIGNMENT_GQL,
  GET_ALGORITHM_VERSIONS_GQL,
  GET_BARN_ALGORITHM_OVERRIDE_GQL,
  GET_BARN_SETTINGS_GQL,
  GET_USER_LIST_GQL,
  LOOKUP_DEVICE_BY_SERIAL_QUERY,
  UPDATE_BARN_MUTATION,
  UPSERT_USER_BARN_TIER_ASSIGNMENT_GQL,
} from './queries';
import NotificationTierSection, {
  UPSERT_BARN_FAULT_SETTINGS_GQL,
  DELETE_BARN_FAULT_SETTINGS_GQL,
  GET_BARN_NOTIFICATION_SETTINGS_GQL,
} from './NotificationTierSection';
import NotificationTierSelector from '../../molecules/NotificationTierSelector/NotificationTierSelector';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import { barnSettingsCheckSchema } from './validation';
import { ToggleSwitchField } from '../../atoms/ToggleSwitch';
import { Beforeunload } from 'react-beforeunload';
import { GetBarnSummaryDataQuery } from '../BarnSummaryTab/queries';
import { InfoIcon, OpenEyeIcon, ClosedEyeIcon, BoxIcon } from '../../atoms/Icons';
import * as Sentry from '@sentry/react';

const shortenAlgorithmVersion = (version) => {
  if (!version) return version;
  return version.length > 8 ? `${version.substring(0, 8)}...` : version;
};

const CALIBRATION_SCHEDULES = [
  {
    key: 'none',
    name: 'None',
    bin_check_reminder_enabled: false,
    bin_check_reminder_in_seconds: 0,
    bin_check_overdue_in_seconds: 0,
  },
  {
    key: 'monthly',
    name: 'Monthly',
    bin_check_reminder_enabled: true,
    bin_check_reminder_in_seconds: 2_592_000,
    bin_check_overdue_in_seconds: 604_800,
  },
];

const CALIBRATION_STYLES = [
  {
    id: CalibrationStyle.ExactAndContinuous,
    name: CalibrationStyleToString[CalibrationStyle.ExactAndContinuous],
  },
  {
    id: CalibrationStyle.Continuous,
    name: CalibrationStyleToString[CalibrationStyle.Continuous],
  },
  {
    id: CalibrationStyle.Exact,
    name: CalibrationStyleToString[CalibrationStyle.Exact],
  },
];
const BIN_CHECK_BARN_SETTING = 'calibration_settings';

// Label constants for consistency between loading and loaded states
const BARN_NAME_LABEL = 'Barn Name:';
const BARN_REFERENCE_ID_LABEL = 'Barn Reference ID:';
const BARN_ADDRESS_LABEL = 'Barn Address:';

const BarnSettingsV2LoadingSkeleton = (
  <Page className="BarnSettingsTabV2-tabContent">
    <TabSection heading="Barn Settings" className="settingsSection">
      <div className="BarnSettingsTabV2-barnSettings">
        <div className="FeedFloTextInput">
          <div className="FeedFloTextInput--label">{BARN_NAME_LABEL}</div>
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
        </div>
        <div className="FeedFloTextInput">
          <div className="FeedFloTextInput--label">{BARN_REFERENCE_ID_LABEL}</div>
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
        </div>
        <div className="FeedFloTextInput">
          <div className="FeedFloTextInput--label">{BARN_ADDRESS_LABEL}</div>
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
        </div>
      </div>

      {/* Bins Loading Skeleton */}
      <div className="BarnSettingsTabV2-skeletonContainer">
        <div className="BarnSettingsTabV2-bin-container-header">
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonHeading" />
        </div>
        <div className="BarnSettingsTabV2-bin-table BarnSettingsTabV2-skeletonContainer">
          <div className="header">#</div>
          <div className="header">Name</div>
          <div className="header">Ref ID</div>
          <div className="header">Bin Capacity</div>
          <div className="header"></div>
          {/* 2 skeleton rows for bins */}
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>

          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>
        </div>
      </div>

      {/* Feed Lines Loading Skeleton */}
      <div className="BarnSettingsTabV2-skeletonContainer">
        <div className="BarnSettingsTabV2-feedline-container-header">
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonHeading" />
        </div>
        <div className="BarnSettingsTabV2-feedline-table BarnSettingsTabV2-skeletonContainer">
          <div className="header">#</div>
          <div className="header">Name</div>
          <div className="header">Ref ID</div>
          <div className="header">Feeders</div>
          <div className="header">Bins</div>
          <div></div>
          {/* 2 skeleton rows for feed lines */}
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonFeeders" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonMultiSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>

          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonFeeders" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonMultiSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>
        </div>
      </div>

      {/* Rooms Loading Skeleton */}
      <div className="BarnSettingsTabV2-skeletonContainer">
        <div className="BarnSettingsTabV2-room-container-header">
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          <LoadingSkeleton className="BarnSettingsTabV2-skeletonHeading" />
        </div>
        <div className="BarnSettingsTabV2-room-table BarnSettingsTabV2-skeletonContainer">
          <div className="header">#</div>
          <div className="header">Name</div>
          <div className="header">Ref ID</div>
          <div className="header">FeedLines</div>
          <div></div>
          {/* 2 skeleton rows for rooms */}
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonMultiSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>

          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonInput" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonMultiSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>
        </div>
      </div>

      {/* Users Loading Skeleton */}
      <LoadingSkeleton className="BarnSettingsTabV2-skeletonHeading" />
      <div className="BarnSettingsTabV2-skeletonContainer">
        <LoadingSkeleton className="BarnSettingsTabV2-skeletonInviteButton" />
        <div className="BarnSettingsTabV2-user-table">
          <div className="header">#</div>
          <div className="header">Name</div>
          <div className="header">Email</div>
          <div className="header">Role</div>
          <div></div>
          {/* 2 skeleton rows for users */}
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonText" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonText" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>

          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonNumber" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonText" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonText" />
          </div>
          <div className="cell">
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonSelect" />
          </div>
          <div>
            <LoadingSkeleton className="BarnSettingsTabV2-skeletonButton" />
          </div>
        </div>
      </div>

      {/* Calibration Settings Loading Skeleton */}
      <LoadingSkeleton className="BarnSettingsTabV2-skeletonHeading" />
      <div className="BarnSettingsTabV2-barnSettings">
        <LoadingSkeleton className="BarnSettingsTabV2-skeletonField" />
        <LoadingSkeleton className="BarnSettingsTabV2-skeletonField" />
      </div>
    </TabSection>
  </Page>
);

// When the user remaps a controller to a 2-bin bin set, align Formik with the bins that
// save will write (sorted binKey → index 0 / 1). Updates dbBinAtIndex* so bin-order radios
// and the two-phase bin order swap match the post-remap DB row assignment.
function syncBinControllerFormBinsAfterRemap(setFieldValue, values, controllerId, binKey, binSetId) {
  const bcIdx = (values.binControllers ?? []).findIndex((c) => c.id === controllerId);
  if (bcIdx < 0) return; // if the controller is not found, return
  const [binId0, binId1] = binKey.split(',');
  const nameById = Object.fromEntries((values.bins ?? []).map((b) => [b.id, b.name]));
  const prefix = `binControllers.${bcIdx}`;
  setFieldValue(`${prefix}.binAtIndex0`, binId0);
  setFieldValue(`${prefix}.binAtIndex1`, binId1);
  setFieldValue(`${prefix}.binAtIndex0Name`, nameById[binId0] ?? '');
  setFieldValue(`${prefix}.binAtIndex1Name`, nameById[binId1] ?? '');
  setFieldValue(`${prefix}.dbBinAtIndex0Id`, binId0);
  setFieldValue(`${prefix}.dbBinAtIndex1Id`, binId1);
  if (binSetId != null) {
    setFieldValue(`${prefix}.binSetId`, binSetId);
  }
}

// Detects bin controllers that will become orphaned by the current form edits.
// A controller is stale when its bin set will be deleted by the save and its two bins
// don't still share a bin set in the new layout (so auto-remap is impossible).
function computeStaleBinControllers(values, initialSettingsData) {
  if (!initialSettingsData?.barn) return [];

  const form_bin_sets = [];
  values.feedLines.forEach((fl) => {
    const match = form_bin_sets.find((bs) => isEqual(bs.bins, new Set(fl.bins)));
    if (match) {
      match.feedLines.add(fl.id);
    } else {
      form_bin_sets.push({ bins: new Set(fl.bins), feedLines: new Set([fl.id]) });
    }
  });
  values.bins.forEach((bin) => {
    if (!values.feedLines.some((fl) => fl.bins.includes(bin.id))) {
      form_bin_sets.push({ bins: new Set([bin.id]), feedLines: new Set() });
    }
  });
  values.feedLines.forEach((fl) => {
    if (fl.bins.length === 0) {
      form_bin_sets.push({ bins: new Set(), feedLines: new Set([fl.id]) });
    }
  });

  const db_bin_sets = (initialSettingsData.barn.bin_sets || []).map((db_bs) => ({
    id: db_bs.id,
    feedLines: new Set((db_bs.feed_lines || []).map((fl) => fl.id)),
    bins: new Set((db_bs.bins || []).map((b) => b.id)),
    raw: db_bs,
  }));

  const stale = [];

  // Identify DB controllers that would become "orphaned" by this save (their 2-bin set no longer
  // exists in the form and can't be auto-remapped), so we can prompt the user to remap them.
  db_bin_sets.forEach((db_bin_set) => {
    const matchingFormBinSet = form_bin_sets.find(
      (fbs) => isEqual(fbs.bins, db_bin_set.bins) && isEqual(fbs.feedLines, db_bin_set.feedLines),
    );
    if (matchingFormBinSet) {
      return; // this bin set survives the save
    }

    const controller = db_bin_set.raw.bin_controllers?.[0];
    if (!controller || controller.deleted_at) {
      return;
    }

    const controllerBinIds = (controller.bin_controller_bins || [])
      .filter((bcb) => !bcb.deleted_at)
      .map((bcb) => bcb.bin_id);

    if (controllerBinIds.length !== 2) {
      return;
    }

    // Auto-remap succeeds when the same 2 bins still share a form bin set
    const canAutoRemap = form_bin_sets.some(
      (fbs) => fbs.bins.size === 2 && controllerBinIds.every((id) => fbs.bins.has(id)),
    );
    if (canAutoRemap) {
      return;
    }

    stale.push({
      id: controller.id,
      deviceId: controller.device_id,
      deviceSerial: controller.device?.serial ?? '',
      binNames: controllerBinIds
        .map((binId) => db_bin_set.raw.bins.find((b) => b.id === binId)?.name ?? binId)
        .join(' / '),
    });
  });

  return stale;
}

// Returns 2-bin form bin sets that don't already have a controller in values.binControllers.
// These are valid reassignment targets for stale controllers (may not exist in the DB yet).
// Each entry: { binIds: [id1, id2], label: 'BinName1 / BinName2', key: 'id1,id2' }
function computeFreeBinSets(values, initialSettingsData, staleControllerIds = new Set()) {
  const allBins = initialSettingsData?.barn?.bins ?? [];
  const binNameById = Object.fromEntries(allBins.map((b) => [b.id, b.name]));

  const form_bin_sets = [];
  values.feedLines.forEach((fl) => {
    const match = form_bin_sets.find((bs) => isEqual(bs.bins, new Set(fl.bins)));
    if (match) {
      match.feedLines.add(fl.id);
    } else {
      form_bin_sets.push({ bins: new Set(fl.bins), feedLines: new Set([fl.id]) });
    }
  });
  values.bins.forEach((bin) => {
    if (!values.feedLines.some((fl) => fl.bins.includes(bin.id))) {
      form_bin_sets.push({ bins: new Set([bin.id]), feedLines: new Set() });
    }
  });
  values.feedLines.forEach((fl) => {
    if (fl.bins.length === 0) {
      form_bin_sets.push({ bins: new Set(), feedLines: new Set([fl.id]) });
    }
  });

  // Only count bins from non-stale controllers as claimed — stale controllers are being
  // reassigned, so their bins are available as part of a new bin set.
  const claimedBinIds = new Set(
    values.binControllers
      .filter((bc) => !staleControllerIds.has(bc.id))
      .flatMap((bc) => [bc.binAtIndex0, bc.binAtIndex1].filter(Boolean)),
  );

  return form_bin_sets
    .filter((fbs) => fbs.bins.size === 2)
    .filter((fbs) => ![...fbs.bins].some((id) => claimedBinIds.has(id)))
    .map((fbs) => {
      const [id1, id2] = [...fbs.bins].sort();
      return {
        key: `${id1},${id2}`,
        binIds: [id1, id2],
        label: [id1, id2].map((id) => binNameById[id] ?? id).join(' / '),
      };
    });
}

// preassignedBinSetIds: Map<sortedBinKey, uuid> — UUIDs generated at user-selection time for
// new bin sets chosen as reassignment targets for stale controllers.  When present, these IDs
// are used instead of generating new ones, guaranteeing the same UUID is used in both the
// upsert_bin_set and remap_bin_controllers in the main UpdateBarn mutation.
function buildVariablesFromFormValues(barn_id, values, initialSettingsData, user, canManageUsers = false, hasStaffAccess = false, barnOverrideData = null, preassignedBinSetIds = new Map()) {
  // Validate required parameters
  if (!barn_id) {
    throw new Error('barn_id is required but was null/undefined');
  }
  if (!initialSettingsData?.barn?.id) {
    throw new Error('initialSettingsData.barn.id is required but was null/undefined');
  }
  if (!initialSettingsData?.barn?.organization_id) {
    throw new Error('initialSettingsData.barn.organization_id is required but was null/undefined');
  }

  const unixNow = dayjs().unix();
  const farm_id = initialSettingsData.barn.id;
  const org_id = initialSettingsData.barn.organization_id;

  const form_bin_sets = [];
  values.feedLines.forEach((fl) => {
    const match = form_bin_sets.find((bs) => {
      return isEqual(bs.bins, new Set(fl.bins));
    });
    if (match) {
      match.feedLines.add(fl.id);
    } else {
      form_bin_sets.push({ bins: new Set(fl.bins), feedLines: new Set([fl.id]) });
    }
  });

  values.bins.forEach((bin) => {
    if (
      !values.feedLines.some((fl) => {
        return fl.bins.includes(bin.id);
      })
    ) {
      form_bin_sets.push({ bins: new Set([bin.id]), feedLines: new Set() });
    }
  });

  values.feedLines.forEach((fl) => {
    if (fl.bins.length === 0) {
      form_bin_sets.push({ bins: new Set(), feedLines: new Set([fl.id]) });
    }
  });

  const db_bin_sets = initialSettingsData?.barn?.bin_sets.map((db_bs) => {
    return {
      id: db_bs.id,
      feedLines: new Set(db_bs.feed_lines.map((fl) => fl.id)),
      bins: new Set(db_bs.bins.map((b) => b.id)),
    };
  });

  const upsert_bin_sets = [];
  const delete_bin_sets = [];
  //Need to delete
  db_bin_sets.forEach((db_bin_set) => {
    const match = form_bin_sets.find((form_bin_set) => {
      return isEqual(form_bin_set.bins, db_bin_set.bins) && isEqual(form_bin_set.feedLines, db_bin_set.feedLines);
    });
    if (!match) {
      delete_bin_sets.push({
        id: db_bin_set.id,
        deleted_at: unixNow,
      });
    }
  });

  //Need to add
  form_bin_sets.forEach((form_bin_set) => {
    const match = db_bin_sets.find((db_bin_set) => {
      return isEqual(form_bin_set.bins, db_bin_set.bins) && isEqual(form_bin_set.feedLines, db_bin_set.feedLines);
    });
    if (!match) {
      // Use a preassigned UUID if the caller reserved one for this bin set (stale-controller
      // remap case) so the same ID is used in upsert_bin_set and remap_bin_controllers.
      const binKey = [...form_bin_set.bins].sort().join(',');
      const newId = preassignedBinSetIds.get(binKey) ?? uuidv4();
      upsert_bin_sets.push({
        id: newId,
        farm_id,
      });
      form_bin_set.id = newId;
    } else {
      form_bin_set.id = match.id;
    }
  });

  const originalBinSetMatrix = {};
  initialSettingsData?.barn?.bin_sets.forEach((bin_set) => {
    const originalBinIds = bin_set.bins.map((b) => b.id);
    const originalFeedLineIds = bin_set.feed_lines.map((fl) => fl.id);

    originalBinIds.forEach((bId) => {
      originalFeedLineIds.forEach((flId) => {
        originalBinSetMatrix[`${bId}-${flId}`] = true;
      });
    });
  });

  const delete_bins = [];

  const upsert_bins = values.bins
    .filter((form_bin) => {
      // filter bins to only include the ones that don't match their initial state
      return !initialSettingsData?.barn?.bins.some((initial_bin) => {
        return (
          initial_bin.id === form_bin.id &&
          Math.abs(initial_bin.capacity_in_grams - convertLargeUnitsToGrams(user.isMetric, form_bin.binCap)) < 20_000 &&
          initial_bin.external_id === form_bin.refId &&
          initial_bin.name === form_bin.name &&
          initial_bin.bin_set_id === form_bin_sets.find((bs) => bs.bins.has(initial_bin.id)).id
        );
      });
    })
    .map((b) => {
      return {
        id: b.id,
        name: b.name,
        external_id: b.refId || null, // setting this here ensures null (not "" empty string) for all changes to avoid unique constraint issues
        capacity_in_grams: convertLargeUnitsToGrams(user.isMetric, b.binCap, 0),
        bin_set_id: form_bin_sets.find((bs) => bs.bins.has(b.id))?.id || null,
        farm_id,
        org_id,
      };
    });
  initialSettingsData?.barn?.bins.forEach((db_bins) => {
    if (!values.bins.map((b) => b.id).includes(db_bins.id)) {
      delete_bins.push({
        id: db_bins.id,
        deleted_at: unixNow,
        capacity_in_grams: 0,
        farm_id,
        org_id,
        external_id: null,
      });
    }
  });

  let upsert_feeders = [];
  let delete_feeders = [];
  let delete_feed_lines = [];
  const upsert_feed_lines = values.feedLines
    .filter((form_fl) => {
      return !initialSettingsData?.barn?.feed_lines.some((initial_fl) => {
        return (
          initial_fl.id === form_fl.id &&
          initial_fl.name === form_fl.name &&
          initial_fl.reference_id === form_fl.refId &&
          initial_fl.bin_set_id === form_bin_sets.find((bs) => bs.feedLines.has(form_fl.id)).id
        );
      });
    })
    .map((fl) => {
      return {
        id: fl.id,
        name: fl.name,
        reference_id: fl.refId?.trim() || null, // setting this here ensures null (not "" empty string) for all changes
        // DB technically avoids checking empty strings but let's not trust the DB to do that either
        bin_set_id: form_bin_sets.find((bs) => bs.feedLines.has(fl.id)).id,
        farm_id,
        org_id,
      };
    });
  initialSettingsData?.barn?.feed_lines.forEach((db_fl) => {
    if (!values.feedLines.map((fl) => fl.id).includes(db_fl.id)) {
      delete_feed_lines.push({
        id: db_fl.id,
        deleted_at: unixNow,
        farm_id,
        org_id,
        name: db_fl.name,
        reference_id: null,
      });
    }
  });

  values.feedLines.forEach((form_fl) => {
    upsert_feeders = upsert_feeders.concat(
      form_fl?.feeders
        ?.filter((form_feeder) => {
          return !initialSettingsData?.barn?.feed_lines
            .find((initial_fl) => {
              return initial_fl.id === form_fl.id;
            })
            ?.feeders.some((initial_feeder) => {
              return (
                initial_feeder.id === form_feeder.id &&
                initial_feeder.quantity === form_feeder.quantity &&
                initial_feeder.capacity_in_grams === form_feeder.capacity_in_grams
              );
            });
        })
        .filter((f) => !!f)
        .map((feeder) => {
          return {
            capacity_in_grams: feeder.capacity_in_grams,
            id: feeder.id,
            quantity: feeder.quantity,
            feed_line_id: feeder.feed_line_id,
          };
        }) || [],
    );
    initialSettingsData?.barn?.feed_lines
      .find((initial_fl) => {
        return initial_fl.id === form_fl.id;
      })
      ?.feeders.forEach((db_feeder) => {
        if (!form_fl.feeders.map((f) => f.id).includes(db_feeder.id)) {
          delete_feeders.push({
            id: db_feeder.id,
            deleted_at: unixNow,
            feed_line_id: db_feeder.feed_line_id,
            quantity: 0,
            capacity_in_grams: 0,
          });
        }
      });
  });

  const delete_bin_feed_lines = [];
  const upsert_bin_feed_lines = [];

  values.feedLines.forEach((form_fl) => {
    const inital_fl = initialSettingsData?.barn?.feed_lines.find((initial_fl) => {
      return initial_fl.id === form_fl.id;
    });
    inital_fl?.bin_feed_lines?.forEach((initial_b_fl) => {
      if (!form_fl.bins.includes(initial_b_fl.bin_id)) {
        delete_bin_feed_lines.push({
          bin_id: initial_b_fl.bin_id,
          feed_line_id: initial_b_fl.feed_line_id,
          deleted_at: unixNow,
        });
      }
    });
    form_fl.bins.forEach((form_binId) => {
      if (!inital_fl?.bin_feed_lines?.map((b_fl) => b_fl.bin_id).includes(form_binId)) {
        upsert_bin_feed_lines.push({ bin_id: form_binId, feed_line_id: form_fl.id, deleted_at: null });
      }
    });
  });

  const { bin_check_reminder_enabled, bin_check_overdue_in_seconds, bin_check_reminder_in_seconds } =
    values.calibrationStyle != CalibrationStyle.Exact
      ? CALIBRATION_SCHEDULES.find((s) => s.key === values.calibrationSchedule) || CALIBRATION_SCHEDULES[0]
      : {};

  const barn_settings = [
    {
      type: BIN_CHECK_BARN_SETTING,
      version: 0,
      farm_id: barn_id,
      settings: {
        calibration_style: values.calibrationStyle,
        bin_check_reminder_enabled,
        bin_check_overdue_in_seconds,
        bin_check_reminder_in_seconds,
      },
    },
    {
      type: 'delivery_autocompelete',
      version: 1,
      farm_id: barn_id,
      settings: {
        active: values.deliveryAutoComplete,
      },
    },
    {
      type: 'notification_animalGroupSnooze',
      version: 1,
      farm_id: barn_id,
      settings: {
        active: values.animalGroupSnooze,
      },
    },
    {
      type: 'bin_inventory_management',
      version: 0,
      farm_id: barn_id,
      settings: {
        feed_order_shipping_time_in_seconds: values.feedOrderShippingTimeInDays * 3600 * 24,
        typical_feed_delivery_size_in_grams: convertLargeUnitsToGrams(
          user.isMetric,
          values.typicalFeedDeliverySizeInTons,
        ),
      },
    },
    {
      type: 'features',
      version: 0,
      farm_id: barn_id,
      settings: {
        bin_inventory_enabled: values.binInventoryEnabled,
      },
    },
    {
      type: 'bin_controller_network',
      version: 0,
      farm_id: barn_id,
      settings: {
        networkProfiles: (values.networkProfiles || [])
          .filter((p) => p.wifiSsid)
          .map((p) => ({
            priority: p.priority,
            wifiSsid: p.wifiSsid,
            ...(p.wifiPassword ? { wifiPassword: p.wifiPassword } : {}),
          })),
      },
    },
  ];

  const upsert_user_barns = [];
  const delete_user_barns = [];

  // Only process user management if user has permission
  if (canManageUsers) {
    // Some users inherit a org or global role and only have a notification tier override
    // We don't want ot insert a new user_barn row for these as that could throw the roles out of sync
    const shouldUpsertUserBarn = (userObj) =>
      userObj.role_id && (!userObj.is_tier_only || userObj.role_editing);

    values.users.filter(shouldUpsertUserBarn).forEach((userObj) => {
        const existingUser = initialSettingsData.barn.user_barns.find(
          (user_barn) => user_barn.user_id === userObj.user_id && user_barn.role_id === userObj.role_id,
        );
        if (!existingUser) {
          upsert_user_barns.push({
            barn_id,
            user_id: userObj.user_id,
            role_id: userObj.role_id,
            deleted_at: null,
          });
        }
      });

    initialSettingsData.barn.user_barns.forEach((user_barn) => {
      const userStillExists = values.users
        .filter(shouldUpsertUserBarn)
        .some((userObj) => userObj.user_id === user_barn.user_id && userObj.role_id === user_barn.role_id);

      if (!userStillExists) {
        delete_user_barns.push({
          barn_id,
          user_id: user_barn.user_id,
          role_id: user_barn.role_id,
          deleted_at: unixNow,
        });
      }
    });
  }

  const upsert_rooms = [];
  const delete_rooms = [];
  const upsert_room_feed_lines = [];
  const delete_room_feed_lines = [];

  // Look for new Rooms and room Feedlines
  values.rooms?.forEach((form_room) => {
    const initial_room = initialSettingsData?.barn.rooms.find((r) => r.id === form_room.id);
    if (initial_room?.name !== form_room.name || initial_room?.reference_id !== form_room.refId) {
      upsert_rooms.push({
        id: form_room.id,
        name: form_room.name,
        reference_id: form_room.refId,
        barn_id: barn_id,
      });
    }
    form_room.feedLines.forEach((fl_id) => {
      if (!initial_room?.room_feed_lines.map((r_fl) => r_fl.id).includes(fl_id)) {
        upsert_room_feed_lines.push({ feed_line_id: fl_id, room_id: form_room.id, deleted_at: null });
      }
    });
  });

  initialSettingsData?.barn.rooms.forEach((initial_room) => {
    const form_room = values.rooms.find((r) => r.id === initial_room.id);
    if (!form_room) {
      delete_rooms.push({ id: initial_room.id, reference_id: null, barn_id: barn_id, deleted_at: unixNow });
    }
    initial_room.room_feed_lines.forEach((r_fl) => {
      if (!form_room?.feedLines.includes(r_fl.feed_line_id)) {
        delete_room_feed_lines.push({ feed_line_id: r_fl.feed_line_id, room_id: initial_room.id, deleted_at: unixNow });
      }
    });
  });

  // Algorithm override handling (staff-only)
  const insert_barn_algorithm_override = [];
  let should_delete_override = false;

  if (hasStaffAccess) {
    const currentOverride = barnOverrideData?.barn_algorithm_override?.[0];
    const formOverrideVersion = values.algorithmOverrideVersion;

    // If there's a current override and we're changing or clearing it, delete it first
    if (currentOverride && (formOverrideVersion === null || formOverrideVersion === '' || currentOverride.algorithm_version !== formOverrideVersion)) {
      should_delete_override = true;
    }

    // If form value is set (not empty) and is new or replacing an existing override, insert it
    if (formOverrideVersion && formOverrideVersion !== '' && (!currentOverride || should_delete_override)) {
      insert_barn_algorithm_override.push({
        barn_id: barn_id,
        algorithm_version: formOverrideVersion,
      });
    }
  }

  // Always return user management variables (they'll be empty arrays if user doesn't have permission)

  // Compute bin_controller bin_set_id remaps for when bin sets are recreated.
  // Only needed when a bin_set is being soft-deleted and a controller references it.
  const _binControllerBinSetRemap = [];
  const _binControllersToOrphan = [];
  const deletedBinSetIds = new Set(delete_bin_sets.map((d) => d.id));

  (initialSettingsData?.barn?.bin_sets || []).forEach((db_bin_set) => {
    const controller = db_bin_set.bin_controllers?.[0];
    if (!controller || controller.deleted_at) return;
    if (!deletedBinSetIds.has(db_bin_set.id)) return;

    const controllerBinIds = (controller.bin_controller_bins || [])
      .filter((bcb) => !bcb.deleted_at)
      .map((bcb) => bcb.bin_id);

    if (controllerBinIds.length !== 2) return;

    const newBinSet = form_bin_sets.find(
      (fbs) => fbs.bins.size === 2 && controllerBinIds.every((binId) => fbs.bins.has(binId)),
    );

    if (newBinSet) {
      _binControllerBinSetRemap.push({ id: controller.id, bin_set_id: newBinSet.id });
    } else {
      _binControllersToOrphan.push(controller.id);
    }
  });

  return {
    barn_id: barn_id,
    barn_external_id: values.barnReferenceId,
    barn_name: values.barnName,
    barn_address: values.barnAddress,
    barn_settings,
    upsert_bins,
    delete_bins,
    upsert_feed_lines,
    delete_feed_lines,
    upsert_feeders,
    delete_feeders,
    upsert_rooms,
    delete_rooms,
    upsert_room_feed_lines,
    delete_room_feed_lines,
    upsert_bin_feed_lines,
    delete_bin_feed_lines,
    upsert_bin_sets,
    delete_bin_sets,
    canManageUsers,
    upsert_user_barns,
    delete_user_barns,
    insert_barn_algorithm_override,
    barn_id_for_override_delete: barn_id, // Always pass barn_id, but use should_execute_delete_override to skip mutation
    deleted_at_timestamp: unixNow,
    should_execute_delete_override: hasStaffAccess && should_delete_override, // Combine both conditions
    hasStaffAccess,
    _binControllerBinSetRemap,
    _binControllersToOrphan,
    standalone_insert_bin_controllers: [],
    standalone_upsert_bin_controller_bins: [],
    clear_device_configuration_updates: [],
    soft_delete_bin_controller_updates: [],
    soft_delete_bin_controller_bin_updates: [],
  };
}

/** Full UpdateBarn variable set with no barn-structure changes — for add/remove controller, immediate remap, etc. */
function buildMinimalUpdateBarnMutationVariables(barnId, initialSettingsData, canManageUsers, hasStaffAccess) {
  const barn = initialSettingsData?.barn;
  return {
    barn_id: barnId,
    barn_external_id: barn?.external_id ?? null,
    barn_name: barn?.name ?? '',
    barn_address: barn?.address ?? null,
    barn_settings: [],
    upsert_bins: [],
    delete_bins: [],
    upsert_feed_lines: [],
    delete_feed_lines: [],
    upsert_bin_feed_lines: [],
    delete_bin_feed_lines: [],
    upsert_bin_sets: [],
    delete_bin_sets: [],
    upsert_feeders: [],
    delete_feeders: [],
    upsert_rooms: [],
    delete_rooms: [],
    upsert_room_feed_lines: [],
    delete_room_feed_lines: [],
    upsert_user_barns: [],
    delete_user_barns: [],
    canManageUsers,
    insert_barn_algorithm_override: [],
    barn_id_for_override_delete: barnId,
    deleted_at_timestamp: 0,
    should_execute_delete_override: false,
    hasStaffAccess,
    remap_bin_controllers: [],
    remap_bin_controller_bins: [],
    swap_bin_controller_bins_phase1: [],
    swap_bin_controller_bins_phase2: [],
    upsert_device_configurations: [],
    standalone_insert_bin_controllers: [],
    standalone_upsert_bin_controller_bins: [],
    clear_device_configuration_updates: [],
    soft_delete_bin_controller_updates: [],
    soft_delete_bin_controller_bin_updates: [],
    insert_device_commands: [],
  };
}

function buildBinControllerDeviceConfig(bc, networkProfiles) {
  const data = {};
  const profiles = (networkProfiles || []).filter((p) => p.wifiSsid);
  if (profiles.length > 0) {
    data.networkProfiles = profiles.map((p) => ({
      priority: p.priority,
      wifiSsid: p.wifiSsid,
      ...(p.wifiPassword ? { wifiPassword: p.wifiPassword } : {}),
      dynamicStaticIp: 'dynamic',
      // Static IP support commented out until firmware supports it:
      // dynamicStaticIp: bc.ipAssignment ?? 'dynamic',
      // ...(bc.ipAssignment === 'static' && bc.staticIpAddress ? { ipV4: bc.staticIpAddress } : {}),
    }));
  }
  if (bc.recoveryTimeSeconds != null) data.shutterFloDelay = Number(bc.recoveryTimeSeconds);
  if (bc.minimumFloPercent != null) data.shutterFloThreshold = Number(bc.minimumFloPercent);
  if (bc.vibrationDelaySeconds != null) data.vibrationDelaySeconds = Number(bc.vibrationDelaySeconds);
  if (bc.vibrationDurationSeconds != null) data.vibrationDurationSeconds = Number(bc.vibrationDurationSeconds);
  if (bc.shutterOpenPercent != null) data.shutterOpenPercent = Number(bc.shutterOpenPercent);
  if (bc.shutterTimeToFullOpenSeconds != null) data.shutterTimeToFullOpen = Number(bc.shutterTimeToFullOpenSeconds);
  if (bc.shutterTimeToFullCloseSeconds != null) data.shutterTimeToFullClose = Number(bc.shutterTimeToFullCloseSeconds);
  return data;
}

const asUserContext = {
  headers: {
    'x-hasura-role': 'user',
  },
};
function BarnSettingsTab({ titleSegments = [], barnId = '' }) {
  const { user, isLoading: userIsLoading } = useUser();
  const { can, canAnywhere } = useCan();

  // Add state for confirmation dialogs
  const confirmationDialogRef = useRef(null);

  // Per-controller advanced section open state (populated when data loads)
  const [bcAdvancedOpen, setBcAdvancedOpen] = useState([]);

  // Track which network profile password fields are revealed
  const [visiblePasswords, setVisiblePasswords] = useState(new Set());

  // State for the "Add Bin Controller" inline form
  const [addBcBinSetId, setAddBcBinSetId] = useState(null);
  const [addBcSerial, setAddBcSerial] = useState('');
  const [wifiEditUnlocked, setWifiEditUnlocked] = useState(false);
  // pendingBcRemaps: { [controllerId]: { binKey: string, preassignedBinSetId: uuid } }
  // Stale controllers reassigned to a not-yet-saved form bin set.  The preassignedBinSetId
  // is generated at selection time so the same UUID is used in upsert_bin_set and in
  // remap_bin_controllers on save — no fragile string-key lookup needed.
  const [pendingBcRemaps, setPendingBcRemaps] = useState({});

  // Check permissions
  const canUpdateBarn = can('barn', barnId, Privilege.BARN_UPDATE);
  const canManageUsers = can('barn', barnId, Privilege.BARN_USERS_MANAGE);
  const hasStaffAccess = canAnywhere('feature', 'feature:staff_access');
  const canUpdateBinController = can('barn', barnId, 'bin_controller:update');

  const {
    loading: settingsLoading,
    data: initialSettingsData,
    error: settingsError,
  } = useQuery(GET_BARN_SETTINGS_GQL, {
    variables: {
      barnId,
      version: 0,
    },
    context: asUserContext,
    fetchPolicy: 'cache-and-network',
  });
  if (settingsError) {
    throw settingsError;
  }

  // Query notification tier settings for initial values
  const { loading: notificationSettingsLoading, data: notificationSettingsData } = useQuery(
    GET_BARN_NOTIFICATION_SETTINGS_GQL,
    {
      variables: { barn_id: barnId, org_id: initialSettingsData?.barn?.organization_id },
      skip: !barnId || !initialSettingsData?.barn?.organization_id,
      fetchPolicy: 'network-only',
      context: asUserContext,
    },
  );
  const {
    loading: userListLoading,
    data: userListData,
    refetch: refetchUserList,
  } = useQuery(GET_USER_LIST_GQL, { context: asUserContext, fetchPolicy: 'cache-and-network' });

  // Staff-only queries for algorithm override
  const {
    loading: algorithmVersionsLoading,
    data: algorithmVersionsData,
  } = useQuery(GET_ALGORITHM_VERSIONS_GQL, {
    skip: !hasStaffAccess,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  const {
    loading: barnOverrideLoading,
    data: barnOverrideData,
  } = useQuery(GET_BARN_ALGORITHM_OVERRIDE_GQL, {
    variables: { barnId },
    skip: !hasStaffAccess,
    context: {
      headers: {
        'x-hasura-role': 'user',
      },
    },
  });

  // Use the static mutation with directives
  const [setupBarnMutation] = useMutation(UPDATE_BARN_MUTATION, {
    refetchQueries: [GET_BARN_SETTINGS_GQL, GetBarnSummaryDataQuery, GET_BARN_NOTIFICATION_SETTINGS_GQL, GET_BARN_ALGORITHM_OVERRIDE_GQL],
    context: asUserContext,
    onError: (error) => {
      console.error(error);
      toast(`Error: ${error.message}`, {
        type: 'error',
        position: 'top-right',
        autoClose: 3000,
        closeOnClick: true,
      });

    },
  });

  const [lookupDeviceBySerial, { data: deviceLookupData, loading: deviceLookupLoading }] = useLazyQuery(
    LOOKUP_DEVICE_BY_SERIAL_QUERY,
    { fetchPolicy: 'no-cache', context: { headers: { 'x-hasura-role': 'user' } } },
  );

  useEffect(() => {
    if (addBcSerial.length < 5) return;
    const timer = setTimeout(() => {
      lookupDeviceBySerial({ variables: { serial: addBcSerial.toUpperCase() } });
    }, 400);
    return () => clearTimeout(timer);
  }, [addBcSerial, lookupDeviceBySerial]);

  // Reset wifi edit lock and pending remaps whenever the server data refreshes (e.g. after a save)
  useEffect(() => {
    setWifiEditUnlocked(false);
    setPendingBcRemaps({});
  }, [initialSettingsData]);
  const [upsertBarnFaultSettings] = useMutation(UPSERT_BARN_FAULT_SETTINGS_GQL, {
    context: asUserContext,
    refetchQueries: [GET_BARN_NOTIFICATION_SETTINGS_GQL],
    onError: (error) => {
      console.error('Error saving barn notification settings:', error);
      Sentry.captureException(error);
      toast(`Error saving notification settings: ${error.message}`, { type: 'error' });
    },
  });

  const [deleteBarnFaultSettings] = useMutation(DELETE_BARN_FAULT_SETTINGS_GQL, {
    context: asUserContext,
    refetchQueries: [GET_BARN_NOTIFICATION_SETTINGS_GQL],
    onError: (error) => {
      console.error('Error removing barn notification overrides:', error);
      Sentry.captureException(error);
      toast(`Error removing notification overrides: ${error.message}`, { type: 'error' });
    },
  });
  const [upsertUserBarnTierAssignment] = useMutation(UPSERT_USER_BARN_TIER_ASSIGNMENT_GQL, {
    context: asUserContext,
    refetchQueries: [GET_BARN_SETTINGS_GQL],
    onError: (error) => {
      console.error('Error updating user policies:', error);
      toast(`Error updating user policies: ${error.message}`, { type: 'error' });
    },
  });
  const [deleteUserBarnTierAssignment] = useMutation(DELETE_USER_BARN_TIER_ASSIGNMENT_GQL, {
    context: asUserContext,
    refetchQueries: [GET_BARN_SETTINGS_GQL],
    onError: (error) => {
      console.error('Error removing user policies:', error);
      toast(`Error removing user policies: ${error.message}`, { type: 'error' });
    },
  });
  // Confirmation dialog handlers
  const [confirmationCallback, setConfirmationCallback] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const showConfirmationDialog = (message, onConfirm) => {
    setConfirmationMessage(message);
    setConfirmationCallback(() => onConfirm);
    if (confirmationDialogRef?.current) {
      confirmationDialogRef.current.showModal();
    }
  };
  const handleConfirmDelete = () => {
    if (confirmationCallback) {
      confirmationCallback();
    }
    setConfirmationCallback(null);
    setConfirmationMessage('');
  };
  const handleCancelDelete = () => {
    setConfirmationCallback(null);
    setConfirmationMessage('');
  };
  const handleUserTierChange = (userId, tierId, setFieldValue, userIndex) => {
    // Update Formik form value to mark form as dirty
    // The actual mutations will be handled in onSubmit
    setFieldValue(`users.${userIndex}.tier_id`, tierId);
  };
  // Handle case where no roles are available
  const availableRoles = initialSettingsData?.roles || [];
  const organizationId = initialSettingsData?.barn?.organization_id || null;
  const currentNotificationPlan = initialSettingsData?.barn?.organization?.selected_notification_plan;
  const planDefaultTierId = useMemo(() => {
    const tiers = currentNotificationPlan?.plan?.tiers || [];
    if (!tiers.length) return null;
    const sorted = [...tiers].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name));
    const noNotificationsTier = sorted.find((tier) => tier.name === NO_NOTIFICATIONS_TIER_NAME);
    return noNotificationsTier?.id || sorted[0]?.id || null;
  }, [currentNotificationPlan]);
  const pageTitleSegments = useMemo(() => ['Settings', ...titleSegments], []);

  // Transform notification tier settings data into form values format
  const initialNotificationTierSettings = useMemo(() => {
    if (!notificationSettingsData?.barn?.fault_notification_settings) {
      return {};
    }
    const currentPlanId = notificationSettingsData?.org_settings?.[0]?.plan_id;
    const barnOverridesForCurrentPlan = notificationSettingsData.barn.fault_notification_settings.filter(
      (setting) => setting.tier?.notification_plan_id === currentPlanId,
    );

    if (barnOverridesForCurrentPlan.length === 0) {
      return {};
    }

    const settings = {};
    barnOverridesForCurrentPlan.forEach((faultSetting) => {
      const tierId = faultSetting.tier.id;
      if (!settings[tierId]) {
        settings[tierId] = {};
      }
      settings[tierId][faultSetting.code.toString()] = {
        code: faultSetting.code,
        alert: {
          enabled: faultSetting.enabled,
          hours: alertThresholdSecondsToHours(faultSetting.alert_threshold_seconds, faultSetting.code),
          type: faultSetting.alert_threshold_type,
          option:
            faultSetting.alert_threshold_type === 'nofeed_time' && faultSetting.alert_threshold_seconds < 0
              ? 'before_empty'
              : faultSetting.alert_threshold_type === 'nofeed_time'
                ? 'after_empty'
                : 'after_duration',
        },
        reminders: {
          enabled: faultSetting.renotify_enabled,
          hours: faultSetting.renotify_threshold_seconds / 3600,
        },
        resolved: {
          enabled: faultSetting.notify_on_resolved,
        },
      };
    });
    return settings;
  }, [notificationSettingsData]);

  if (
    settingsLoading ||
    userIsLoading ||
    userListLoading ||
    (hasStaffAccess && (algorithmVersionsLoading || barnOverrideLoading)) ||
    notificationSettingsLoading ||
    typeof user.isMetric != 'boolean' ||
    !organizationId ||
    !barnId
  ) {
    // Moved loading skeleton out of component as it's very long don't forget to style both as needed
    return BarnSettingsV2LoadingSkeleton;
  }

  // Bin sets that have exactly 2 bins but no active bin controller (candidates for adding one)
  const binSetsWithoutController = (initialSettingsData?.barn?.bin_sets || []).filter(
    (bs) => bs.bins?.length === 2 && bs.bin_controllers?.length === 0,
  );

  // The device found by the current serial input (cleared when input changes)
  const foundDevice = addBcSerial.length >= 5 ? (deviceLookupData?.device?.[0] ?? null) : null;

  const handleRemoveBinController = (id, deviceId, serial, isDirty) => {
    if (isDirty) {
      toast('Please save your changes before modifying bin controllers.', {
        type: 'warning',
        position: 'top-right',
        autoClose: 4000,
        closeOnClick: true,
      });
      return;
    }
    showConfirmationDialog(
      <div>
        <div className="ConfirmationDialog-messageHeading">Warning</div>
        <div className="ConfirmationDialog-messageLine">
          Removing this bin controller{serial ? ` (${serial})` : ''} will delete its settings at this barn.
        </div>
        <div className="ConfirmationDialog-messageLine">
          This may cause the device to lose connectivity, and you may not be able to update it again without on-site
          support to recover.
        </div>
        <div className="ConfirmationDialog-messageLine">Are you sure you want to remove this bin controller?</div>
      </div>,
      async () => {
        try {
          const now = dayjs().unix();
          await setupBarnMutation({
            variables: {
              ...buildMinimalUpdateBarnMutationVariables(barnId, initialSettingsData, canManageUsers, hasStaffAccess),
              soft_delete_bin_controller_updates: [
                { where: { id: { _eq: id } }, _set: { deleted_at: now } },
              ],
              soft_delete_bin_controller_bin_updates: [
                {
                  where: { bin_controller_id: { _eq: id }, deleted_at: { _is_null: true } },
                  _set: { deleted_at: now },
                },
              ],
              clear_device_configuration_updates: [
                {
                  where: {
                    device_id: { _eq: deviceId },
                    ended_at: { _is_null: true },
                    deleted_at: { _is_null: true },
                  },
                  _set: { ended_at: now },
                },
              ],
              insert_device_commands: [{ device_id: deviceId, type: 'Set Configuration', data: {} }],
            },
          });
          toast('Bin controller removed.', { position: 'top-right', autoClose: 3000, closeOnClick: true });
        } catch (err) {
          console.error(err);
          Sentry.captureException(err);
          toast(`Error removing bin controller: ${err.message}`, {
            type: 'error',
            position: 'top-right',
            autoClose: 4000,
            closeOnClick: true,
          });
        }
      },
    );
  };

  const handleAddBinController = async (isDirty) => {
    if (isDirty) {
      toast('Please save your changes before adding a bin controller.', {
        type: 'warning',
        position: 'top-right',
        autoClose: 4000,
        closeOnClick: true,
      });
      return;
    }
    if (!foundDevice || !addBcBinSetId) return;
    const binSet = (initialSettingsData?.barn?.bin_sets || []).find((bs) => bs.id === addBcBinSetId);
    if (!binSet || binSet.bins.length !== 2) return;
    const existingBinControllerId = foundDevice.bin_controller?.id ?? null;
    try {
      await setupBarnMutation({
        variables: {
          ...buildMinimalUpdateBarnMutationVariables(barnId, initialSettingsData, canManageUsers, hasStaffAccess),
          standalone_insert_bin_controllers: [
            {
              device_id: foundDevice.id,
              bin_set_id: addBcBinSetId,
              deleted_at: null,
              // First-time pairing: no bin_controller row yet — nested insert creates junction rows.
              // Reactivation: row exists (soft-deleted) with old junction rows still enforcing
              // UNIQUE(bin_controller_id, index); use standalone_upsert_bin_controller_bins instead.
              ...(existingBinControllerId
                ? {}
                : {
                  bin_controller_bins: {
                    data: [
                      { bin_id: binSet.bins[0].id, index: 0 },
                      { bin_id: binSet.bins[1].id, index: 1 },
                    ],
                    on_conflict: {
                      constraint: 'bin_controller_bin_controller_index_unique',
                      update_columns: ['bin_id', 'deleted_at'],
                    },
                  },
                }),
            },
          ],
          standalone_upsert_bin_controller_bins: existingBinControllerId
            ? [
              {
                bin_controller_id: existingBinControllerId,
                index: 0,
                bin_id: binSet.bins[0].id,
                deleted_at: null,
              },
              {
                bin_controller_id: existingBinControllerId,
                index: 1,
                bin_id: binSet.bins[1].id,
                deleted_at: null,
              },
            ]
            : [],
          insert_device_commands: [
            { device_id: foundDevice.id, type: 'Set Configuration', data: {} },
          ],
        },
      });
      toast('Bin controller added successfully.', { position: 'top-right', autoClose: 3000, closeOnClick: true });
      setAddBcBinSetId(null);
      setAddBcSerial('');
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      toast(`Error adding bin controller: ${err.message}`, {
        type: 'error',
        position: 'top-right',
        autoClose: 4000,
        closeOnClick: true,
      });
    }
  };

  // Reminder if you change this layout also change BarnSettingsV2LoadingSkeleton at the top of this file
  return (
    <Page className="BarnSettingsTabV2-tabContent" titleSegments={pageTitleSegments}>
      <ConfirmationDialog
        dialogRef={confirmationDialogRef}
        message={confirmationMessage}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        confirmColor={Colors.Danger}
      />
      <Formik
        enableReinitialize={true}
        validationSchema={barnSettingsCheckSchema}
        initialValues={{
          barnName: initialSettingsData?.barn?.name,
          barnReferenceId: initialSettingsData?.barn?.external_id,
          barnAddress: initialSettingsData?.barn?.address,
          calibrationStyle: initialSettingsData?.barn_setting[0]?.settings?.calibration_style || CalibrationStyle.Exact,
          calibrationSchedule: CALIBRATION_SCHEDULES.find(
            (schedule) =>
              schedule.bin_check_reminder_in_seconds ===
              initialSettingsData?.barn_setting[0]?.settings?.bin_check_reminder_in_seconds,
          )?.key || CALIBRATION_SCHEDULES[0].key,
          users: (() => {
            const usersFromUserBarns = (initialSettingsData?.barn?.user_barns || []).map((user_barn) => {
              const orgAccess = user_barn.user?.user_organizations?.[0];
              return {
                user_id: user_barn.user_id,
                role_id: user_barn.role_id,
                tier_id: user_barn.user_barn_notification_tier_assignment?.tier_id || null,
                tier_assignment_id: user_barn.user_barn_notification_tier_assignment?.id || null,
                org_tier_id: orgAccess?.user_organization_notification_tier_assignment?.tier_id || null,
                inherited_org_role_id: null,
                inherited_org_role_name: null,
                is_tier_only: false,
                role_editing: false,
              };
            });

            const existingUserIds = new Set((initialSettingsData?.barn?.user_barns || []).map((ub) => ub.user_id));

            const usersFromTierAssignments = (initialSettingsData?.barn?.user_notification_tier_assignments || [])
              .filter((tierAssignment) => !existingUserIds.has(tierAssignment.user_id))
              .map((tierAssignment) => {
                const orgAccess = tierAssignment.user?.user_organizations?.[0];
                return {
                  user_id: tierAssignment.user_id,
                  role_id: null,
                  tier_id: tierAssignment.tier_id || null,
                  tier_assignment_id: tierAssignment.id || null,
                  org_tier_id: orgAccess?.user_organization_notification_tier_assignment?.tier_id || null,
                  inherited_org_role_id: orgAccess?.role?.id || null,
                  inherited_org_role_name: orgAccess?.role?.name || null,
                  is_tier_only: true,
                  role_editing: false,
                };
              });

            return [...usersFromUserBarns, ...usersFromTierAssignments];
          })(),
          bins: initialSettingsData?.barn?.bins?.map((b) => {
            return {
              id: b.id,
              name: b.name,
              refId: b.external_id,
              binCap: convertGramsToLargeUnits(user?.isMetric, b.capacity_in_grams),
            };
          }),
          feedLines: initialSettingsData?.barn?.feed_lines?.map((fl) => {
            return {
              id: fl.id,
              name: fl.name,
              refId: fl.reference_id,
              bins: fl.bin_assignment.map((bin_assignment) => bin_assignment.bin_id),
              feeders: fl?.feeders?.map((feeder) => {
                return {
                  id: feeder.id,
                  capacity_in_grams: feeder.capacity_in_grams,
                  quantity: feeder.quantity,
                  feed_line_id: fl.id,
                };
              }),
            };
          }),
          rooms: initialSettingsData?.barn?.rooms.map((room) => {
            return {
              id: room.id,
              name: room.name,
              refId: room.reference_id,
              feedLines: room.room_feed_lines?.map((r_fl) => r_fl.feed_line_id) || [],
            };
          }),
          deliveryAutoComplete: initialSettingsData?.auto_complete_delivery_setting[0]?.settings?.active === true,
          animalGroupSnooze: initialSettingsData?.notification_animalGroupSnooze_setting[0]?.settings?.active === true,
          feedOrderShippingTimeInDays: initialSettingsData?.bin_inventory_management_setting[0]?.settings
            ?.feed_order_shipping_time_in_seconds
            ? initialSettingsData.bin_inventory_management_setting[0].settings.feed_order_shipping_time_in_seconds /
            (3600 * 24)
            : 2,
          typicalFeedDeliverySizeInTons: initialSettingsData?.bin_inventory_management_setting[0]?.settings
            ?.typical_feed_delivery_size_in_grams
            ? convertGramsToLargeUnits(
              user?.isMetric,
              initialSettingsData.bin_inventory_management_setting[0].settings.typical_feed_delivery_size_in_grams,
            )
            : 12,
          binInventoryEnabled: initialSettingsData?.features_setting[0]?.settings?.bin_inventory_enabled === true,
          algorithmOverrideVersion: hasStaffAccess && barnOverrideData?.barn_algorithm_override?.[0]
            ? barnOverrideData.barn_algorithm_override[0].algorithm_version
            : null,
          networkProfiles: (() => {
            const normaliseProfile = (p) => ({
              priority: p.priority ?? 1,
              wifiSsid: p.wifiSsid ?? '',
              wifiPassword: p.wifiPassword ?? '',
            });

            // Preferred: barn-level setting (written by this UI on first save)
            const barnProfiles = initialSettingsData?.bin_controller_network_setting?.[0]?.settings?.networkProfiles;
            if (barnProfiles?.length > 0) {
              return barnProfiles.map(normaliseProfile);
            }

            // Fallback: read from the first device that has networkProfiles in its config.
            // Existing barns store Wi-Fi credentials only in device_configuration.data before
            // this UI was built, so we surface them here so the user sees what's already deployed.
            const firstDeviceProfiles = (initialSettingsData?.barn?.bin_sets ?? [])
              .flatMap((bs) => bs.bin_controllers ?? [])
              .map((bc) => bc.device?.device_configurations?.[0]?.data?.networkProfiles)
              .find((profiles) => profiles?.length > 0);

            if (firstDeviceProfiles?.length > 0) {
              return firstDeviceProfiles.map(normaliseProfile);
            }

            return [];
          })(),
          binControllers: (initialSettingsData?.barn?.bin_sets || [])
            .filter((bs) => bs.bin_controllers?.length > 0)
            .map((bs) => {
              const bc = bs.bin_controllers[0];
              const bcbs = bc.bin_controller_bins ?? [];
              const rowAt0 = bcbs.find((r) => r.index === 0);
              const rowAt1 = bcbs.find((r) => r.index === 1);
              const existingConfig = bc.device?.device_configurations?.[0]?.data ?? {};
              return {
                id: bc.id,
                deviceId: bc.device_id,
                deviceSerial: bc.device?.serial ?? '',
                binSetId: bc.bin_set_id,
                binAtIndex0: rowAt0?.bin_id ?? null,
                binAtIndex1: rowAt1?.bin_id ?? null,
                dbBinAtIndex0Id: rowAt0?.bin_id ?? null,
                dbBinAtIndex1Id: rowAt1?.bin_id ?? null,
                dbBinAtIndex0RowId: rowAt0?.id ?? null,
                dbBinAtIndex1RowId: rowAt1?.id ?? null,
                binAtIndex0Name: rowAt0?.bin?.name ?? '',
                binAtIndex1Name: rowAt1?.bin?.name ?? '',
                recoveryTimeSeconds: existingConfig.shutterFloDelay ?? 120,
                vibrationDelaySeconds: existingConfig.vibrationDelaySeconds ?? 2,
                vibrationDurationSeconds: existingConfig.vibrationDurationSeconds ?? 5,
                minimumFloPercent: existingConfig.shutterFloThreshold ?? 70,
                shutterOpenPercent: existingConfig.shutterOpenPercent ?? 100,
                shutterTimeToFullOpenSeconds: existingConfig.shutterTimeToFullOpen ?? 6,
                shutterTimeToFullCloseSeconds: existingConfig.shutterTimeToFullClose ?? 6,
                // Static IP support commented out until firmware supports it:
                // ipAssignment: existingConfig.networkProfiles?.[0]?.dynamicStaticIp ?? 'dynamic',
                // staticIpAddress: existingConfig.networkProfiles?.[0]?.ipV4 ?? '',
              };
            }),
          notificationTierSettings: initialNotificationTierSettings,
          notificationTierSettingsDelete: false,
        }}
        onSubmit={async (values) => {
          // Don't allow submission if user doesn't have permission
          if (!canUpdateBarn) {
            toast('You do not have permission to update barn settings', {
              type: 'error',
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
            });
            return;
          }

          // Build the preassigned UUID map so buildVariablesFromFormValues uses the same UUID
          // that remap_bin_controllers will use on save — no post-save key-lookup needed.
          const preassignedBinSetIds = new Map(
            Object.values(pendingBcRemaps)
              .filter((r) => r?.preassignedBinSetId)
              .map((r) => [r.binKey, r.preassignedBinSetId]),
          );

          let buildResult = null;
          try {
            buildResult = buildVariablesFromFormValues(barnId, values, initialSettingsData, user, canManageUsers, hasStaffAccess, barnOverrideData, preassignedBinSetIds);
          } catch (error) {
            toast(`Error: ${error.message}`, {
              type: 'error',
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
            });
            return;
          }

          const { _binControllerBinSetRemap, _binControllersToOrphan, ...mutationVariables } = buildResult;

          // Allow save only when every orphaned controller has been assigned a target bin set.
          const unresolved = _binControllersToOrphan.filter((id) => !pendingBcRemaps[id]?.preassignedBinSetId);
          if (unresolved.length > 0) {
            toast(
              'Cannot save: one or more bin controllers need to be reassigned or removed before saving.',
              { type: 'error', position: 'top-right', autoClose: 8000, closeOnClick: true },
            );
            return;
          }

          // Build the combined remap list — both auto-remapped and user-chosen stale controllers.
          // Uses update_bin_controller_many so each entry is a { where, _set } pair (no id in payload).
          // Included in the main mutation so the FK update is atomic with the new bin set creation.
          const remap_bin_controllers = [
            ..._binControllerBinSetRemap.map(({ id, bin_set_id }) => ({
              where: { id: { _eq: id } },
              _set: { bin_set_id },
            })),
            ...Object.entries(pendingBcRemaps)
              .filter(([, remap]) => remap?.preassignedBinSetId)
              .map(([controllerId, remap]) => ({
                where: { id: { _eq: controllerId } },
                _set: { bin_set_id: remap.preassignedBinSetId },
              })),
          ];

          // For user-selected remaps, also update bin_controller_bin to point at the new bins
          // so that binAtIndex0Name / binAtIndex1Name reflect the correct bins after refetch.
          // binKey is always "sortedBinId0,sortedBinId1" for 2-bin systems.
          //
          // Row ordering matters: on_conflict only defers its own target constraint
          // (bin_controller_bin_controller_index_unique). bin_controller_bin_bin_unique (on bin_id)
          // is checked immediately per row. If the new bin set reuses one of the old bin_ids
          // (e.g. old {A,B} → new {B,C}), inserting index=0 with bin_id=B while index=1 still
          // holds bin_id=B causes a transient violation. Fix: when new[0].bin_id == old[1].bin_id,
          // emit index=1 first so it "frees" the shared bin_id before index=0 claims it.
          const remap_bin_controller_bins = Object.entries(pendingBcRemaps)
            .filter(([, remap]) => remap?.preassignedBinSetId && remap?.binKey)
            .flatMap(([controllerId, remap]) => {
              const [binId0, binId1] = remap.binKey.split(',');
              const bc = values.binControllers.find((c) => c.id === controllerId);
              const row0 = { bin_controller_id: controllerId, index: 0, bin_id: binId0, deleted_at: null };
              const row1 = { bin_controller_id: controllerId, index: 1, bin_id: binId1, deleted_at: null };
              // If the new bin at index 0 is the same as the current bin at index 1, process
              // index 1 first to free that bin_id before index 0 claims it.
              if (bc && binId0 === bc.dbBinAtIndex1Id) return [row1, row0];
              return [row0, row1];
            });
          // Build bin order swap (see mutation comment): two statements in one GraphQL request.
          const binControllersSwappingOrder = (canUpdateBinController ? values.binControllers : []).filter(
            (bc) => bc.binAtIndex0 !== bc.dbBinAtIndex0Id && bc.dbBinAtIndex0RowId && bc.dbBinAtIndex1RowId,
          );
          const swap_bin_controller_bins_phase1 = binControllersSwappingOrder.flatMap((bc) => [
            { id: bc.dbBinAtIndex0RowId, bin_controller_id: bc.id, bin_id: bc.dbBinAtIndex0Id, index: 2 },
            { id: bc.dbBinAtIndex1RowId, bin_controller_id: bc.id, bin_id: bc.dbBinAtIndex1Id, index: 3 },
          ]);
          const swap_bin_controller_bins_phase2 = binControllersSwappingOrder.flatMap((bc) => [
            { id: bc.dbBinAtIndex1RowId, bin_controller_id: bc.id, bin_id: bc.dbBinAtIndex1Id, index: 0 },
            { id: bc.dbBinAtIndex0RowId, bin_controller_id: bc.id, bin_id: bc.dbBinAtIndex0Id, index: 1 },
          ]);

          // Build device config upserts for controllers whose config blob changed.
          const controllersWithOrderChange = new Set(binControllersSwappingOrder.map((bc) => bc.id));
          const controllersNeedingConfigUpdate = canUpdateBinController
            ? (values.binControllers ?? []).filter((bc) => {
              const currentConfig =
                (initialSettingsData?.barn?.bin_sets ?? [])
                  .flatMap((bs) => bs.bin_controllers ?? [])
                  .find((c) => c.id === bc.id)
                  ?.device?.device_configurations?.[0]?.data ?? {};
              const newConfig = buildBinControllerDeviceConfig(bc, values.networkProfiles);
              return !isEqual(currentConfig, newConfig);
            })
            : [];
          const now = dayjs().unix();
          const upsert_device_configurations = controllersNeedingConfigUpdate.map((bc) => ({
            device_id: bc.deviceId,
            version: 1,
            started_at: now,
            ended_at: null,
            data: buildBinControllerDeviceConfig(bc, values.networkProfiles),
          }));

          // Build "Set Configuration" commands for every controller with a config or order change.
          const commandRecipientIds = new Set([
            ...controllersNeedingConfigUpdate.map((bc) => bc.id),
            ...controllersWithOrderChange,
          ]);
          const insert_device_commands = (canUpdateBinController ? values.binControllers : [])
            .filter((bc) => commandRecipientIds.has(bc.id))
            .map((bc) => ({ device_id: bc.deviceId, type: 'Set Configuration', data: {} }));

          const mainMutationVariables = {
            ...mutationVariables,
            remap_bin_controllers,
            remap_bin_controller_bins,
            swap_bin_controller_bins_phase1,
            swap_bin_controller_bins_phase2,
            upsert_device_configurations,
            insert_device_commands,
          };

          // Process tier assignment changes if user has permission
          const tierAssignmentPromises = [];
          if (canManageUsers) {
            values.users.forEach((userObj) => {
              // Check for tier assignment from user_barn relationship
              const initialUserBarn = initialSettingsData?.barn?.user_barns?.find(
                (ub) => ub.user_id === userObj.user_id,
              );
              let initialTierId = initialUserBarn?.user_barn_notification_tier_assignment?.tier_id || null;
              let initialAssignmentId = initialUserBarn?.user_barn_notification_tier_assignment?.id || null;

              // If not found in user_barn, check tier assignments directly
              if (!initialAssignmentId) {
                const tierAssignment = initialSettingsData?.barn?.user_notification_tier_assignments?.find(
                  (ta) => ta.user_id === userObj.user_id,
                );
                initialTierId = tierAssignment?.tier_id || null;
                initialAssignmentId = tierAssignment?.id || null;
              }

              const newTierId = (() => {
                if (userObj.tier_id) return userObj.tier_id;
                if (initialTierId === null && (userObj.role_id || userObj.is_tier_only)) {
                  return resolveDefaultTierId({
                    orgTierId: userObj.org_tier_id,
                    planDefaultTierId,
                  });
                }
                return null;
              })();

              if (initialTierId !== newTierId) {
                if (newTierId === null && initialAssignmentId) {
                  tierAssignmentPromises.push(
                    deleteUserBarnTierAssignment({
                      variables: { id: initialAssignmentId },
                    }).catch((error) => {
                      console.warn('Delete tier assignment mutation not available, skipping deletion:', error);
                      Sentry.captureException(error);
                      return { data: { affected_rows: 0 } };
                    }),
                  );
                } else if (newTierId !== null) {
                  tierAssignmentPromises.push(
                    upsertUserBarnTierAssignment({
                      variables: {
                        objects: [
                          {
                            user_id: userObj.user_id,
                            tier_id: newTierId,
                            barn_id: barnId,
                          },
                        ],
                      },
                    }),
                  );
                }
              }
            });

            // Delete tier assignments for users who were removed from the form entirely.
            // Combine assignments sourced from user_barns and from user_notification_tier_assignments,
            // deduping by user_id since a user_barn assignment takes precedence when both exist.
            const initialTierAssignmentsByUserId = new Map();
            (initialSettingsData?.barn?.user_barns ?? []).forEach((ub) => {
              const assignmentId = ub?.user_barn_notification_tier_assignment?.id;
              if (assignmentId) {
                initialTierAssignmentsByUserId.set(ub.user_id, assignmentId);
              }
            });
            (initialSettingsData?.barn?.user_notification_tier_assignments ?? []).forEach((ta) => {
              if (ta?.id && !initialTierAssignmentsByUserId.has(ta.user_id)) {
                initialTierAssignmentsByUserId.set(ta.user_id, ta.id);
              }
            });

            initialTierAssignmentsByUserId.forEach((assignmentId, userId) => {
              const userStillExists = values.users.some((userObj) => userObj.user_id === userId);
              if (!userStillExists) {
                tierAssignmentPromises.push(
                  deleteUserBarnTierAssignment({
                    variables: { id: assignmentId },
                  }).catch((error) => {
                    console.warn('Delete tier assignment mutation not available, skipping deletion:', error);
                    Sentry.captureException(error);
                    return { data: { affected_rows: 0 } };
                  }),
                );
              }
            });
          }

          // Handle notification tier settings mutations only if they've changed
          const notificationTierPromises = [];
          const initialHasOverrides =
            initialNotificationTierSettings && Object.keys(initialNotificationTierSettings).length > 0;
          const currentHasOverrides =
            values.notificationTierSettings && Object.keys(values.notificationTierSettings).length > 0;
          const hasChanged =
            JSON.stringify(initialNotificationTierSettings) !== JSON.stringify(values.notificationTierSettings) ||
            (values.notificationTierSettingsDelete && initialHasOverrides);

          if (hasChanged) {
            if (values.notificationTierSettingsDelete || (!currentHasOverrides && initialHasOverrides)) {
              // Delete all barn overrides
              notificationTierPromises.push(deleteBarnFaultSettings({ variables: { barn_id: barnId } }));
            } else if (currentHasOverrides) {
              // Upsert barn overrides
              const allObjects = [];
              Object.entries(values.notificationTierSettings).forEach(([tierId, faultSettings]) => {
                if (!faultSettings || typeof faultSettings !== 'object') return;
                const objects = Object.entries(faultSettings).map(([code, setting]) => ({
                  notification_tier_id: tierId,
                  code: parseInt(code, 10),
                  barn_id: barnId,
                  enabled: setting.alert?.enabled,
                  alert_threshold_seconds: alertHoursToThresholdSeconds(setting.alert?.hours, code),
                  alert_threshold_type: setting.alert?.type,
                  renotify_enabled: setting.reminders?.enabled,
                  renotify_threshold_seconds: (setting.reminders?.hours || 0) * 3600,
                  notify_on_resolved: setting.resolved?.enabled,
                }));
                allObjects.push(...objects);
              });
              if (allObjects.length > 0) {
                notificationTierPromises.push(upsertBarnFaultSettings({ variables: { objects: allObjects } }));
              }
            }
          }

          try {
            const results = await Promise.allSettled([
              setupBarnMutation({ variables: mainMutationVariables }),
              ...tierAssignmentPromises,
              ...notificationTierPromises,
            ]);

            const failures = results.filter((result) => result.status === 'rejected');
            if (failures.length > 0) {
              const errorMessages = failures.map((f) => f.reason?.message || 'Unknown error').join(', ');
              console.error('Some mutations failed:', failures);
              Sentry.captureException(new Error(`Mutation failures: ${errorMessages}`));
              toast(`Error: ${errorMessages}`, {
                type: 'error',
                position: 'top-right',
                autoClose: 3000,
                closeOnClick: true,
              });
              return;
            }
          } catch (error) {
            console.error(error);
            Sentry.captureException(error);
            toast(`Error: ${error.message}`, {
              type: 'error',
              position: 'top-right',
              autoClose: 3000,
              closeOnClick: true,
            });
            return;
          }

          toast('Barn Settings Saved', {
            position: 'top-right',
            autoClose: 3000,
            closeOnClick: true,
          });
        }}
      >
        {({ values, handleSubmit, dirty, isValid, setFieldValue }) => (
          <Form>
            {!canUpdateBarn && (
              <div className="BarnSettingsTabV2-viewOnlyBanner">
                <strong>View Only Mode:</strong> You do not have permission to update barn settings. Need Help? Contact
                support@feedflo.com
              </div>
            )}
            {dirty && canUpdateBarn && (
              <UnsavedChangesBar isValid={isValid} loading={settingsLoading} onSaveClick={handleSubmit} />
            )}
            {dirty && canUpdateBarn && <Beforeunload onBeforeunload={(event) => event.preventDefault()} />}
            <TabSection heading="Barn Settings" className="settingsSection">
              <div className="BarnSettingsTabV2-barnSettings">
                <TextInputField name="barnName" label={BARN_NAME_LABEL} displayOnly={!canUpdateBarn} />
                <TextInputField name="barnReferenceId" label={BARN_REFERENCE_ID_LABEL} displayOnly={!canUpdateBarn} />
                <TextInputField name="barnAddress" label={BARN_ADDRESS_LABEL} displayOnly={!canUpdateBarn} />
              </div>
              <div className="BarnSettingsTabV2-bin-container">
                <FieldArray name="bins">
                  {({ remove, push }) => (
                    <React.Fragment key="bins">
                      <div className="BarnSettingsTabV2-bin-container-header">
                        {canUpdateBarn && (
                          <Button
                            color="success"
                            variant="vivid"
                            content="+"
                            onClick={() => {
                              push({
                                name: `Bin ${values.bins.length + 1}`,
                                id: uuidv4(),
                                binCap: 15,
                                refId: null, // setting this here ensures null (not "" empty string) ONLY for new barns
                              });
                            }}
                          />
                        )}
                        <Heading className="TabSection-heading">Bins</Heading>
                      </div>
                      <div className="BarnSettingsTabV2-bin-table">
                        <div className="header">#</div>
                        <div className="header">Name</div>
                        <div className="header">Ref ID</div>
                        <div className="header">Bin Capacity ({weightLargeUnitLabel(user?.isMetric)})</div>
                        <div className="header"></div>
                        {values.bins?.length > 0 &&
                          values.bins?.map((bin, index) => (
                            <React.Fragment key={`bin-${index}`}>
                              <div className="cell index">{index + 1}</div>
                              <div className="cell">
                                <TextInputField name={`bins.${index}.name`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                <TextInputField name={`bins.${index}.refId`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                <NumberInputField
                                  name={`bins.${index}.binCap`}
                                  displayOnly={!canUpdateBarn}
                                ></NumberInputField>
                              </div>
                              <div className="cell">
                                {canUpdateBarn ? (
                                  <Button
                                    content="x"
                                    variant="text"
                                    color="error"
                                    className="remove"
                                    onClick={() => {
                                      showConfirmationDialog(
                                        <div>
                                          <div style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '8px' }}>
                                            Warning: Deleting a Bin can cause issues with integrations and historical
                                            data.
                                          </div>
                                          <div style={{ marginBottom: '8px' }}>
                                            Contact Support@FeedFlo.com if you have any questions.
                                          </div>
                                          <div>Are you sure you want to delete bin &quot;{bin.name}&quot;?</div>
                                        </div>,
                                        () => remove(index),
                                      );
                                    }}
                                  />
                                ) : (
                                  <div></div>
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                      </div>
                      {/* Helper text when insufficient bins or feed lines */}
                      {!isValid && (values.bins?.length < 1 || values.feedLines?.length < 1) && (
                        <div className="BarnSettingsTabV2-helperText">
                          <p>Please add at least one bin and one feed line before saving</p>
                        </div>
                      )}
                    </React.Fragment>
                  )}
                </FieldArray>
              </div>
              <div className="BarnSettingsTabV2-feedline-container">
                <FieldArray name="feedLines">
                  {({ remove, push }) => (
                    <>
                      <div className="BarnSettingsTabV2-feedline-container-header">
                        {canUpdateBarn && (
                          <Button
                            color="success"
                            variant="vivid"
                            content="+"
                            onClick={() => {
                              const new_fl_id = uuidv4();
                              push({
                                id: new_fl_id,
                                bins: [],
                                name: `Feed Line ${values.feedLines.length + 1}`,
                                feeders: [
                                  { id: uuidv4(), feed_line_id: new_fl_id, quantity: 10, capacity_in_grams: 226796 },
                                ],
                              });
                            }}
                          />
                        )}
                        <Heading className="TabSection-heading">Feed Lines</Heading>
                      </div>
                      <div className="BarnSettingsTabV2-feedline-table">
                        <div className="header">#</div>
                        <div className="header">Name</div>
                        <div className="header">Ref ID</div>
                        <div className="header">Feeders</div>
                        <div className="header">Bins</div>
                        <div></div>
                        {values.feedLines?.length > 0 &&
                          values.feedLines?.map((fl, index) => (
                            <React.Fragment key={`feedLine-${index}`}>
                              <div className="cell">{index + 1}</div>
                              <div className="cell">
                                <TextInputField name={`feedLines.${index}.name`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                <TextInputField name={`feedLines.${index}.refId`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                <FieldArray name={`feedLines.${index}.feeders`}>
                                  {({ remove, push }) => (
                                    <React.Fragment key={`feedLine-${index}-feeders`}>
                                      {values.feedLines[index]?.feeders?.map((feeder, feederIndex) => (
                                        <div key={`${feederIndex}`} className="FeederEditCell">
                                          <div>
                                            ({feeder?.quantity ? feeder.quantity : '--'}){' '}
                                            {feeder?.capacity_in_grams
                                              ? convertGramsToSmallUnits(user.isMetric, feeder?.capacity_in_grams, 0)
                                              : '--'}
                                            {weightSmallUnitLabel(user.isMetric)}
                                          </div>
                                          {canUpdateBarn && (
                                            <>
                                              <EditFeederModalTrigger
                                                feedLineName={values.feedLines[index].name}
                                                feedLineIndex={index}
                                                feederIndex={feederIndex}
                                              />
                                              <Button
                                                type="primary"
                                                content="X"
                                                variant="text"
                                                color="error"
                                                onClick={() => {
                                                  remove();
                                                }}
                                              />
                                            </>
                                          )}
                                        </div>
                                      ))}
                                      {values.feedLines[index]?.feeders.length === 0 && <div>No Feeders</div>}
                                      {canUpdateBarn && (
                                        <Button
                                          type="primary"
                                          content="+"
                                          variant="text"
                                          color="success"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            push({
                                              id: uuidv4(),
                                              feed_line_id: fl.id,
                                              quantity: 10,
                                              capacity_in_grams: 226796,
                                            });
                                          }}
                                        />
                                      )}
                                    </React.Fragment>
                                  )}
                                </FieldArray>
                              </div>
                              <div className="cell">
                                { }
                                <ComboboxMultiField
                                  name={`feedLines.${index}.bins`}
                                  key={`feedLines.${index}.bins`}
                                  emptyPlaceholder="Select Bins"
                                  items={values?.bins?.map((bin) => {
                                    return { name: bin?.name, id: bin?.id };
                                  })}
                                  loading={false}
                                  itemNameSingular="Barn"
                                  itemNamePlural="Barns"
                                  allowSelectAll={false}
                                  displayOnly={!canUpdateBarn}
                                  textSummaryStyle="list"
                                />
                              </div>
                              <div>
                                {canUpdateBarn ? (
                                  <Button
                                    content="x"
                                    variant="text"
                                    color="error"
                                    className="remove"
                                    onClick={() => {
                                      showConfirmationDialog(
                                        <div>
                                          <div style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '8px' }}>
                                            Warning: Deleting a Feed Line can cause issues with integrations and
                                            historical data.
                                          </div>
                                          <div style={{ marginBottom: '8px' }}>
                                            Contact Support@FeedFlo.com if you have any questions.
                                          </div>
                                          <div>Are you sure you want to delete feed line &quot;{fl.name}&quot;?</div>
                                        </div>,
                                        () => remove(index),
                                      );
                                    }}
                                  />
                                ) : (
                                  <div></div>
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                      </div>
                      {/* Helper text when insufficient bins or feed lines */}
                      {!isValid && (values.bins?.length < 1 || values.feedLines?.length < 1) && (
                        <div className="BarnSettingsTabV2-helperText">
                          <p>Please add at least one bin and one feed line before saving</p>
                        </div>
                      )}
                    </>
                  )}
                </FieldArray>
              </div>

              {/* Wi-Fi & Networks section — staff only; shown when at least one bin controller is configured */}
              {hasStaffAccess && values.binControllers.length > 0 && (
                <div className="BarnSettingsTabV2-bc-network-container">
                  <div className="BarnSettingsTabV2-bc-network-header">
                    <div className="BarnSettingsTabV2-bc-network-title-row">
                      <Heading className="TabSection-heading">Wi-Fi &amp; Networks</Heading>
                      {canUpdateBinController && !wifiEditUnlocked && (
                        <button
                          type="button"
                          className="BarnSettingsTabV2-bc-network-edit-btn"
                          aria-label="Edit Wi-Fi settings"
                          title="Edit Wi-Fi settings"
                          onClick={() =>
                            showConfirmationDialog(
                              <div>
                                <div className="ConfirmationDialog-messageHeading">Warning</div>
                                <div className="ConfirmationDialog-messageLine">
                                  Changing Wi-Fi credentials will affect all bin controllers at this barn. Incorrect
                                  credentials may cause devices to lose connectivity and require on-site support to
                                  recover.
                                </div>
                                <div className="ConfirmationDialog-messageLine">
                                  Are you sure you want to edit these settings?
                                </div>
                              </div>,
                              () => setWifiEditUnlocked(true),
                            )
                          }
                        >
                          <PencilIconSVG />
                        </button>
                      )}
                    </div>
                    <p className="BarnSettingsTabV2-bc-network-hint">
                      These credentials apply to all bin controllers at this barn.
                    </p>
                  </div>
                  {wifiEditUnlocked && (
                    <div className="BarnSettingsTabV2-bc-network-warning">
                      <span>
                        Incorrect Wi-Fi credentials may disconnect all bin controllers at this barn.
                      </span>
                    </div>
                  )}
                  <FieldArray name="networkProfiles">
                    {({ remove: removeProfile, push: pushProfile }) => (
                      <>
                        {canUpdateBinController && wifiEditUnlocked && (
                          <Button
                            color="success"
                            variant="vivid"
                            content="+ Add Network"
                            onClick={() =>
                              pushProfile({
                                priority: values.networkProfiles.length + 1,
                                dynamicStaticIp: 'dynamic',
                                wifiSsid: '',
                                wifiPassword: '',
                                ipV4: '',
                              })
                            }
                          />
                        )}
                        {values.networkProfiles.map((profile, profileIndex) => (
                          <div key={profileIndex} className="BarnSettingsTabV2-bc-network-card">
                            <div className="BarnSettingsTabV2-bc-network-card-header">
                              <span className="BarnSettingsTabV2-bc-network-card-title">
                                Network {profileIndex + 1}
                              </span>
                              {canUpdateBinController && wifiEditUnlocked && (
                                <Button
                                  content="Remove"
                                  color="error"
                                  variant="text"
                                  onClick={() => removeProfile(profileIndex)}
                                />
                              )}
                            </div>
                            <NumberInputField
                              name={`networkProfiles.${profileIndex}.priority`}
                              label={
                                <span className="BarnSettingsTabV2-bc-network-priority-label">
                                  Priority
                                  <FeedFloToolTip
                                    description={<>1 = preferred network (connects first)<br />2 = first fallback, etc.</>}
                                    multiline
                                    position="top"
                                    textClassName="BarnSettingsTabV2-bc-network-priority-tooltip"
                                  >
                                    <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                  </FeedFloToolTip>
                                </span>
                              }
                              min={1}
                              displayOnly={!canUpdateBinController || !wifiEditUnlocked}
                            />
                            <TextInputField
                              name={`networkProfiles.${profileIndex}.wifiSsid`}
                              label="Network Name (SSID)"
                              displayOnly={!canUpdateBinController || !wifiEditUnlocked}
                            />
                            {wifiEditUnlocked && (
                              <TextInputField
                                name={`networkProfiles.${profileIndex}.wifiPassword`}
                                label="Password"
                                type={visiblePasswords.has(profileIndex) ? 'text' : 'password'}
                                right={
                                  !canUpdateBinController ? null : (
                                    <button
                                      type="button"
                                      className="BarnSettingsTabV2-bc-eye-toggle"
                                      aria-label={visiblePasswords.has(profileIndex) ? 'Hide password' : 'Show password'}
                                      onClick={() =>
                                        setVisiblePasswords((prev) => {
                                          const next = new Set(prev);
                                          if (next.has(profileIndex)) next.delete(profileIndex);
                                          else next.add(profileIndex);
                                          return next;
                                        })
                                      }
                                    >
                                      {visiblePasswords.has(profileIndex) ? (
                                        <OpenEyeIcon />
                                      ) : (
                                        <ClosedEyeIcon />
                                      )}
                                    </button>
                                  )
                                }
                              />
                            )}
                          </div>
                        ))}
                      </>
                    )}
                  </FieldArray>
                </div>
              )}

              {/* Per-controller cards + add-controller for 2-bin sets without a controller */}
              {(() => {
                const allStaleBinControllers = computeStaleBinControllers(values, initialSettingsData);
                // Controllers with a resolved pending remap are treated as "back in the regular
                // list" immediately — they leave the stale section before save.
                const staleBinControllers = allStaleBinControllers.filter(
                  (s) => !pendingBcRemaps[s.id]?.preassignedBinSetId,
                );
                const staleControllerIdSet = new Set(allStaleBinControllers.map((s) => s.id));
                // Controllers that have been reassigned but not yet saved should appear in the
                // regular list, so exclude them from the "stale" rendering set only.
                const staleRenderIdSet = new Set(staleBinControllers.map((s) => s.id));
                return (
                  <>
                    {staleBinControllers.length > 0 && (() => {
                      const freeBinSets = computeFreeBinSets(values, initialSettingsData, staleControllerIdSet);
                      const allReassignOptions = [
                        // Existing DB bin sets without a controller — can be reassigned immediately
                        ...binSetsWithoutController.map((bs) => ({
                          key: (bs.bins || []).map((b) => b.id).sort().join(','),
                          label: (bs.bins || []).map((b) => b.name).join(' / '),
                          existingBinSetId: bs.id,
                        })),
                        // New form bin sets (not yet in DB) — will be resolved on save
                        ...freeBinSets
                          .filter((p) => !binSetsWithoutController.some(
                            (bs) => (bs.bins || []).map((b) => b.id).sort().join(',') === p.key,
                          ))
                          .map((p) => ({
                            key: p.key,
                            label: `${p.label} (new bin set)`,
                            existingBinSetId: null,
                          })),
                      ];
                      return (
                        <div className="BarnSettingsTabV2-bc-stale-section">
                          <Heading className="TabSection-heading">Bin Controllers</Heading>
                          <div className="BarnSettingsTabV2-bc-stale-banner">
                            {staleBinControllers.length === 1
                              ? 'A bin controller is attached to a bin set that will be removed by your current changes.'
                              : `${staleBinControllers.length} bin controllers are attached to bin sets that will be removed by your current changes.`}
                            {' '}Remove or reassign each one before saving.
                          </div>
                          {staleBinControllers.map((stale) => {
                            const selectedRemap = pendingBcRemaps[stale.id];
                            const selectedKey = selectedRemap?.binKey ?? null;
                            return (
                              <div key={stale.id} className="BarnSettingsTabV2-bc-card BarnSettingsTabV2-bc-card--stale">
                                <div className="BarnSettingsTabV2-bc-card-header">
                                  <span>Bin Controller</span>
                                  {stale.deviceSerial && (
                                    <span className="BarnSettingsTabV2-bc-card-serial"> · {stale.deviceSerial}</span>
                                  )}
                                  {stale.binNames && (
                                    <span className="BarnSettingsTabV2-bc-card-subtitle"> · {stale.binNames}</span>
                                  )}
                                </div>
                                <div className="BarnSettingsTabV2-bc-stale-actions">
                                  <span className="BarnSettingsTabV2-bc-stale-label">
                                    Bin set removed — reassign or remove this controller:
                                  </span>
                                  <div className="BarnSettingsTabV2-bc-stale-controls">
                                    <select
                                      className="BarnSettingsTabV2-bc-stale-select"
                                      value={selectedKey ?? ''}
                                      onChange={async (e) => {
                                        const key = e.target.value;
                                        if (!key) return;
                                        const option = allReassignOptions.find((o) => o.key === key);
                                        if (!option) return;
                                        if (option.existingBinSetId) {
                                          // Existing DB bin set — reassign immediately (same transaction shape as full save)
                                          try {
                                            await setupBarnMutation({
                                              variables: {
                                                ...buildMinimalUpdateBarnMutationVariables(
                                                  barnId,
                                                  initialSettingsData,
                                                  canManageUsers,
                                                  hasStaffAccess,
                                                ),
                                                remap_bin_controllers: [
                                                  {
                                                    where: { id: { _eq: stale.id } },
                                                    _set: { bin_set_id: option.existingBinSetId },
                                                  },
                                                ],
                                              },
                                            });
                                            syncBinControllerFormBinsAfterRemap(
                                              setFieldValue,
                                              values,
                                              stale.id,
                                              key,
                                              option.existingBinSetId,
                                            );
                                            toast('Bin controller reassigned.', {
                                              position: 'top-right',
                                              autoClose: 3000,
                                              closeOnClick: true,
                                            });
                                          } catch (err) {
                                            console.error(err);
                                            Sentry.captureException(err);
                                            toast(`Error reassigning controller: ${err.message}`, {
                                              type: 'error',
                                              position: 'top-right',
                                              autoClose: 4000,
                                              closeOnClick: true,
                                            });
                                          }
                                        } else {
                                          // New bin set — generate the UUID now so the same ID
                                          // is used in upsert_bin_set and remap_bin_controllers.
                                          const preassignedBinSetId = uuidv4();
                                          setPendingBcRemaps((prev) => ({
                                            ...prev,
                                            [stale.id]: { binKey: key, preassignedBinSetId },
                                          }));
                                          syncBinControllerFormBinsAfterRemap(
                                            setFieldValue,
                                            values,
                                            stale.id,
                                            key,
                                            preassignedBinSetId,
                                          );
                                        }
                                      }}
                                    >
                                      <option value="" disabled>Reassign to bin set…</option>
                                      {allReassignOptions.map((o) => (
                                        <option key={o.key} value={o.key}>{o.label}</option>
                                      ))}
                                    </select>
                                    <button
                                      type="button"
                                      className="BarnSettingsTabV2-bc-remove-btn"
                                      onClick={() => handleRemoveBinController(stale.id, stale.deviceId, stale.deviceSerial, false)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  {selectedRemap?.preassignedBinSetId && (
                                    <p className="BarnSettingsTabV2-bc-stale-pending-note">
                                      Will be reassigned when you save.
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                    {(values.binControllers.some((bc) => !staleRenderIdSet.has(bc.id)) ||
                      (canUpdateBinController && binSetsWithoutController.length > 0)) && (
                        <div className="BarnSettingsTabV2-bc-controllers-container">
                          <Heading className="TabSection-heading">Bin Controllers</Heading>
                          {values.binControllers.filter((bc) => !staleRenderIdSet.has(bc.id)).map((controller, bcIndex) => (
                            <div key={controller.id} className="BarnSettingsTabV2-bc-card">
                              {(() => {
                                const pendingRemap = pendingBcRemaps[controller.id];
                                const allBinNames = Object.fromEntries(
                                  (initialSettingsData?.barn?.bins ?? []).map((b) => [b.id, b.name]),
                                );
                                const pendingBinLabel = pendingRemap?.binKey
                                  ? pendingRemap.binKey.split(',').map((id) => allBinNames[id] ?? id).join(' / ')
                                  : null;
                                return (
                                  <div className="BarnSettingsTabV2-bc-card-header">
                                    Bin Controller
                                    {controller.deviceSerial && (
                                      <span className="BarnSettingsTabV2-bc-card-serial"> · {controller.deviceSerial}</span>
                                    )}
                                    {pendingBinLabel ? (
                                      <span className="BarnSettingsTabV2-bc-card-subtitle">
                                        {' '}· {pendingBinLabel}
                                        <span className="BarnSettingsTabV2-bc-card-pending-badge"> (reassigning on save)</span>
                                      </span>
                                    ) : (
                                      controller.binAtIndex0Name && controller.binAtIndex1Name && (
                                        <span className="BarnSettingsTabV2-bc-card-subtitle">
                                          {' '}
                                          · {controller.binAtIndex0Name} / {controller.binAtIndex1Name}
                                        </span>
                                      )
                                    )}
                                    {canUpdateBinController && (
                                      <button
                                        type="button"
                                        className="BarnSettingsTabV2-bc-remove-btn"
                                        onClick={() => handleRemoveBinController(controller.id, controller.deviceId, controller.deviceSerial, dirty)}
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Slide Open % — always visible */}
                              <div className="BarnSettingsTabV2-bc-card-section">
                                <div className="BarnSettingsTabV2-bc-slider-row">
                                  <span className="BarnSettingsTabV2-bc-slider-label">
                                    Slide Open % ({controller.shutterOpenPercent ?? 0}%)
                                    <FeedFloToolTip
                                      description="When clearing a bridge, the slide will open to 100% regardless of this setting."
                                      multiline
                                      position="top"
                                    >
                                      <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                    </FeedFloToolTip>
                                  </span>
                                  <input
                                    type="range"
                                    className="BarnSettingsTabV2-bc-slider"
                                    min={0}
                                    max={100}
                                    step={1}
                                    disabled={!canUpdateBinController}
                                    value={controller.shutterOpenPercent ?? 0}
                                    onChange={(e) =>
                                      setFieldValue(`binControllers.${bcIndex}.shutterOpenPercent`, Number(e.target.value))
                                    }
                                  />
                                </div>
                              </div>

                              {/* Advanced section */}
                              <div className="BarnSettingsTabV2-bc-card-section">
                                <button
                                  type="button"
                                  className="BarnSettingsTabV2-bc-advanced-toggle"
                                  onClick={() =>
                                    setBcAdvancedOpen((prev) => {
                                      const next = [...prev];
                                      next[bcIndex] = !next[bcIndex];
                                      return next;
                                    })
                                  }
                                >
                                  {bcAdvancedOpen[bcIndex] ? 'Advanced ▲' : 'Advanced ▼'}
                                </button>
                                {bcAdvancedOpen[bcIndex] && (
                                  <div className="BarnSettingsTabV2-bc-advanced">
                                    <div className="BarnSettingsTabV2-bc-card-label">Bin Order</div>
                                    <div className="BarnSettingsTabV2-bc-bin-order">
                                      <label className="BarnSettingsTabV2-bc-bin-order-option">
                                        <input
                                          type="radio"
                                          disabled={!canUpdateBinController}
                                          checked={controller.binAtIndex0 === controller.dbBinAtIndex0Id}
                                          onChange={() => {
                                            setFieldValue(`binControllers.${bcIndex}.binAtIndex0`, controller.dbBinAtIndex0Id);
                                            setFieldValue(`binControllers.${bcIndex}.binAtIndex1`, controller.dbBinAtIndex1Id);
                                          }}
                                        />
                                        <span>{controller.binAtIndex0Name} first</span>
                                      </label>
                                      <label className="BarnSettingsTabV2-bc-bin-order-option">
                                        <input
                                          type="radio"
                                          disabled={!canUpdateBinController}
                                          checked={controller.binAtIndex0 === controller.dbBinAtIndex1Id}
                                          onChange={() => {
                                            setFieldValue(`binControllers.${bcIndex}.binAtIndex0`, controller.dbBinAtIndex1Id);
                                            setFieldValue(`binControllers.${bcIndex}.binAtIndex1`, controller.dbBinAtIndex0Id);
                                          }}
                                        />
                                        <span>{controller.binAtIndex1Name} first</span>
                                      </label>
                                    </div>
                                    <NumberInputField
                                      name={`binControllers.${bcIndex}.recoveryTimeSeconds`}
                                      label={
                                        <span className="BarnSettingsTabV2-bc-field-label-with-tooltip">
                                          Recovery Time (seconds)
                                          <FeedFloToolTip
                                            description="Time it takes for feed to travel from the bin to the FLO sensor on the pipe. We recommend adding about 30 seconds as a buffer."
                                            multiline
                                            position="top"
                                            textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                          >
                                            <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                          </FeedFloToolTip>
                                        </span>
                                      }
                                      min={0}
                                      max={65535}
                                      displayOnly={!canUpdateBinController}
                                    />
                                    <NumberInputField
                                      name={`binControllers.${bcIndex}.vibrationDelaySeconds`}
                                      label={
                                        <span className="BarnSettingsTabV2-bc-field-label-with-tooltip">
                                          Vibration Delay (seconds)
                                          <FeedFloToolTip
                                            description="Waits before vibrating so the bin is not vibrating while the slide is still closed."
                                            multiline
                                            position="top"
                                            textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                          >
                                            <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                          </FeedFloToolTip>
                                        </span>
                                      }
                                      min={0}
                                      max={65535}
                                      displayOnly={!canUpdateBinController}
                                    />
                                    <NumberInputField
                                      name={`binControllers.${bcIndex}.vibrationDurationSeconds`}
                                      label={
                                        <span className="BarnSettingsTabV2-bc-field-label-with-tooltip">
                                          Vibration Duration (seconds)
                                          <FeedFloToolTip
                                            description="How long the bin vibrates each time an empty pipe is detected."
                                            multiline
                                            position="top"
                                            textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                          >
                                            <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                          </FeedFloToolTip>
                                        </span>
                                      }
                                      min={0}
                                      max={65535}
                                      displayOnly={!canUpdateBinController}
                                    />
                                    <div className="BarnSettingsTabV2-bc-slider-row">
                                      <span className="BarnSettingsTabV2-bc-slider-label">
                                        Minimum Flo % ({controller.minimumFloPercent ?? 0}%)
                                        <FeedFloToolTip
                                          description="When feed in the pipe falls below this level, it is treated as an empty pipe."
                                          multiline
                                          position="top"
                                          textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                        >
                                          <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                        </FeedFloToolTip>
                                      </span>
                                      <input
                                        type="range"
                                        className="BarnSettingsTabV2-bc-slider"
                                        min={0}
                                        max={100}
                                        step={1}
                                        disabled={!canUpdateBinController}
                                        value={controller.minimumFloPercent ?? 0}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `binControllers.${bcIndex}.minimumFloPercent`,
                                            Number(e.target.value),
                                          )
                                        }
                                      />
                                    </div>
                                    <NumberInputField
                                      name={`binControllers.${bcIndex}.shutterTimeToFullOpenSeconds`}
                                      label={
                                        <span className="BarnSettingsTabV2-bc-field-label-with-tooltip">
                                          Time to Fully Open (seconds)
                                          <FeedFloToolTip
                                            description="Seconds for the slide to open completely. Time this on-site after installation so it matches your hardware."
                                            multiline
                                            position="top"
                                            textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                          >
                                            <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                          </FeedFloToolTip>
                                        </span>
                                      }
                                      min={0}
                                      max={65535}
                                      displayOnly={!canUpdateBinController}
                                    />
                                    <NumberInputField
                                      name={`binControllers.${bcIndex}.shutterTimeToFullCloseSeconds`}
                                      label={
                                        <span className="BarnSettingsTabV2-bc-field-label-with-tooltip">
                                          Time to Fully Close (seconds)
                                          <FeedFloToolTip
                                            description="Seconds for the slide to close completely. Time this on-site after installation so it matches your hardware."
                                            multiline
                                            position="top"
                                            textClassName="BarnSettingsTabV2-bc-advanced-field-tooltip"
                                          >
                                            <InfoIconSVG className="BarnSettingsTabV2-bc-info-icon" />
                                          </FeedFloToolTip>
                                        </span>
                                      }
                                      min={0}
                                      max={65535}
                                      displayOnly={!canUpdateBinController}
                                    />
                                    {/* Static IP support — commented out until firmware supports it
                              <SelectField
                                name={`binControllers.${bcIndex}.ipAssignment`}
                                label="IP Assignment"
                                displayOnly={!canUpdateBinController}
                                itemList={[
                                  { id: 'dynamic', name: 'DHCP (automatic)' },
                                  { id: 'static', name: 'Static IP' },
                                ]}
                              />
                              {controller.ipAssignment === 'static' && (
                                <TextInputField
                                  name={`binControllers.${bcIndex}.staticIpAddress`}
                                  label="IP Address"
                                  displayOnly={!canUpdateBinController}
                                />
                              )}
                              */}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}

                          {/* Add-controller cards for 2-bin sets that don't yet have a controller */}
                          {canUpdateBinController &&
                            binSetsWithoutController.map((bs) => (
                              <div key={bs.id} className="BarnSettingsTabV2-bc-card BarnSettingsTabV2-bc-card--add">
                                {addBcBinSetId !== bs.id ? (
                                  <div className="BarnSettingsTabV2-bc-add-idle">
                                    <span className="BarnSettingsTabV2-bc-add-label">
                                      {bs.bins.map((b) => b.name).join(' / ')}
                                    </span>
                                    <Button
                                      color="success"
                                      variant="vivid"
                                      content="+ Add Bin Controller"
                                      onClick={() => {
                                        if (dirty) {
                                          toast('Please save your changes before adding a bin controller.', {
                                            type: 'warning',
                                            position: 'top-right',
                                            autoClose: 4000,
                                            closeOnClick: true,
                                          });
                                          return;
                                        }
                                        setAddBcBinSetId(bs.id);
                                        setAddBcSerial('');
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="BarnSettingsTabV2-bc-add-form">
                                    <p className="BarnSettingsTabV2-bc-add-form-title">
                                      Add Bin Controller · {bs.bins.map((b) => b.name).join(' / ')}
                                    </p>
                                    <div className="BarnSettingsTabV2-bc-add-serial-row">
                                      <TextInput
                                        label="Serial Number"
                                        name="addBcSerial"
                                        value={addBcSerial}
                                        onChange={(e) => setAddBcSerial(e.target.value.trim())}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                                        autoFocus
                                      />
                                      {deviceLookupLoading && (
                                        <span className="BarnSettingsTabV2-bc-add-status">Searching…</span>
                                      )}
                                      {!deviceLookupLoading && addBcSerial.length >= 5 && foundDevice && (
                                        <span className="BarnSettingsTabV2-bc-add-status BarnSettingsTabV2-bc-add-status--found">
                                          Device found: {foundDevice.serial}
                                        </span>
                                      )}
                                      {!deviceLookupLoading && addBcSerial.length >= 5 && !foundDevice && (
                                        <span className="BarnSettingsTabV2-bc-add-status BarnSettingsTabV2-bc-add-status--not-found">
                                          Device not found or already active on another bin controller
                                        </span>
                                      )}
                                    </div>
                                    <div className="BarnSettingsTabV2-bc-add-actions">
                                      <Button
                                        content="Add"
                                        color="success"
                                        variant="vivid"
                                        disabled={!foundDevice}
                                        onClick={() => handleAddBinController(dirty)}
                                      />
                                      <Button
                                        content="Cancel"
                                        color="error"
                                        variant="text"
                                        onClick={() => {
                                          setAddBcBinSetId(null);
                                          setAddBcSerial('');
                                        }}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                  </>
                );
              })()}

              <div className="BarnSettingsTabV2-room-container">
                <FieldArray name="rooms">
                  {({ remove, push }) => (
                    <React.Fragment key="rooms">
                      <div className="BarnSettingsTabV2-room-container-header">
                        {canUpdateBarn && (
                          <Button
                            color="success"
                            variant="vivid"
                            content="+"
                            onClick={() => {
                              const id = uuidv4();
                              push({
                                id,
                                name: '',
                                refId: '',
                                feedlines: [],
                              });
                            }}
                          />
                        )}
                        <Heading className="TabSection-heading">Rooms</Heading>
                      </div>
                      <div className="BarnSettingsTabV2-room-table">
                        <div className="header">#</div>
                        <div className="header">Name</div>
                        <div className="header">Ref ID</div>
                        <div className="header">FeedLines</div>
                        <div></div>
                        {values.rooms?.length > 0 &&
                          values.rooms?.map((fl, index) => (
                            <React.Fragment key={`room-${index}`}>
                              <div className="cell">{index + 1}</div>
                              <div className="cell">
                                <TextInputField name={`rooms.${index}.name`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                <TextInputField name={`rooms.${index}.refId`} displayOnly={!canUpdateBarn} />
                              </div>
                              <div className="cell">
                                { }
                                <ComboboxMultiField
                                  name={`rooms.${index}.feedLines`}
                                  key={`rooms.${index}.feedLines`}
                                  emptyPlaceholder="Select FeedLines"
                                  items={values?.feedLines?.map((feedLine) => {
                                    return { name: feedLine?.name, id: feedLine?.id };
                                  })}
                                  loading={false}
                                  allowSelectAll={false}
                                  displayOnly={!canUpdateBarn}
                                  textSummaryStyle="list"
                                />
                              </div>
                              <div>
                                {canUpdateBarn ? (
                                  <Button
                                    content="x"
                                    variant="text"
                                    color="error"
                                    className="remove"
                                    onClick={() => {
                                      showConfirmationDialog(
                                        <div>
                                          <div style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '8px' }}>
                                            Warning: Deleting a Room can cause issues with integrations and historical
                                            data.
                                          </div>
                                          <div style={{ marginBottom: '8px' }}>
                                            Contact Support@FeedFlo.com if you have any questions.
                                          </div>
                                          <div>Are you sure you want to delete room &quot;{fl.name}&quot;?</div>
                                        </div>,
                                        () => remove(index),
                                      );
                                    }}
                                  />
                                ) : (
                                  <div></div>
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                      </div>
                    </React.Fragment>
                  )}
                </FieldArray>
              </div>
              <Heading className="TabSection-heading">Users</Heading>
              <div className="BarnSettingsTabV2-user-container">
                <FieldArray name={`users`}>
                  {({ remove, push }) => (
                    <React.Fragment key="users">
                      <InviteButton
                        className="BarnSettingsTabV2-invites"
                        barnId={barnId}
                        organizationId={organizationId}
                        disableOrgInvites={true}
                        hideButton={true}
                        availableRoles={availableRoles}
                        onUserSelected={(user) => {
                          if (!user.isNewUser && canUpdateBarn) {
                            if (values.users.some((existingUser) => existingUser.user_id === user.id)) {
                              toast.warn('User is already in the list');
                              return;
                            }

                            const orgAccess = user.user_organizations?.[0];
                            const hasOrgAccess = !!orgAccess?.role?.id;
                            const hasBarnRoleAtThisBarn = user.user_barns?.some(
                              (userBarn) => userBarn.barn_id === barnId && userBarn.role_id,
                            );

                            if (hasBarnRoleAtThisBarn) {
                              toast.warn('User already has barn access');
                              return;
                            }

                            if (hasOrgAccess) {
                              push({
                                user_id: user.id,
                                role_id: null,
                                tier_id: null,
                                tier_assignment_id: null,
                                org_tier_id:
                                  orgAccess.user_organization_notification_tier_assignment?.tier_id || null,
                                inherited_org_role_id: orgAccess.role.id,
                                inherited_org_role_name: orgAccess.role.name,
                                is_tier_only: true,
                                role_editing: false,
                                isNewlyAdded: true,
                              });
                              return;
                            }

                            push({
                              user_id: user.id,
                              role_id: null,
                              tier_id: null,
                              tier_assignment_id: null,
                              org_tier_id: null,
                              inherited_org_role_id: null,
                              inherited_org_role_name: null,
                              is_tier_only: false,
                              role_editing: false,
                              isNewlyAdded: true,
                            });
                          }
                        }}
                        onInviteCreated={async (results) => {
                          // if we created a new invite (which implicitly assigns them to barns/users)
                          if (canUpdateBarn && results?.barns?.find((b) => b.barnId === barnId)) {
                            await refetchUserList();
                            push({
                              barn_id: barnId,
                              user_id: results?.user?.id,
                              name: results?.user?.name,
                              email: results?.user?.email,
                              role_id: results?.barns?.find((b) => b.barnId === barnId)?.roleId,
                              tier_id: null,
                              tier_assignment_id: null,
                              org_tier_id: null,
                              inherited_org_role_id: null,
                              inherited_org_role_name: null,
                              is_tier_only: false,
                              role_editing: false,
                              isNewlyAdded: true,
                              isAlreadySaved: true,
                            });
                          }
                        }}
                        suppressModal={true}
                      />
                      <div className="BarnSettingsTabV2-user-table">
                        <div className="header">#</div>
                        <div className="header">Name</div>
                        <div className="header">Email</div>
                        <div className="header">Role</div>
                        <div className="header">Notification Policy</div>
                        <div></div>
                        {values.users?.map((userObj, index) => {
                          const cachedUser = userListData.user.find((user) => user.id == userObj.user_id) || {};
                          const user = { ...cachedUser, ...userObj }; // spread and overwrite to avoid requerying all settings
                          const selectedRole = initialSettingsData?.roles?.find((role) => role.id === userObj.role_id);
                          const isNewUser = userObj.isNewlyAdded;
                          const isNewlySaved = isNewUser && userObj.isAlreadySaved;

                          if (Object.keys(cachedUser).length === 0) {
                            return null;
                          }
                          let statusIndicator = '';
                          if (isNewlySaved) {
                            statusIndicator = '✅';
                          } else if (isNewUser) {
                            statusIndicator = '✨';
                          }
                          return (
                            <React.Fragment key={`user-${index}`}>
                              <div className={`cell ${isNewUser ? 'BarnSettingsTabV2-userRow--new' : ''}`}>
                                {statusIndicator}
                                {index + 1}
                              </div>
                              <div className={`cell ${isNewUser ? 'BarnSettingsTabV2-userRow--new' : ''}`}>
                                {user?.id ? (
                                  <Link
                                    to={
                                      organizationId ? `/orgs/${organizationId}/users/${user.id}` : `/users/${user.id}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="BarnSettingsTabV2-userLink"
                                  >
                                    {user?.name}
                                    <BoxIcon size={14} className="BarnSettingsTabV2-externalLinkIcon" />
                                  </Link>
                                ) : (
                                  <>{user?.name}</>
                                )}
                              </div>
                              <div className={`cell ${isNewUser ? 'BarnSettingsTabV2-userRow--new' : ''}`}>
                                {user?.email}
                              </div>
                              <div className={`cell ${isNewUser ? 'BarnSettingsTabV2-userRow--new' : ''}`}>
                                {canManageUsers ? (
                                  <div>
                                    {userObj.is_tier_only &&
                                    userObj.inherited_org_role_name &&
                                    !userObj.role_editing ? (
                                      <div className="BarnSettingsTabV2-inheritedRole">
                                        <span className="BarnSettingsTabV2-inheritedRole-label">
                                          Org: {userObj.inherited_org_role_name}
                                        </span>
                                        <button
                                          type="button"
                                          className="BarnSettingsTabV2-inheritedRole-edit"
                                          disabled={!canUpdateBarn}
                                          onClick={() => setFieldValue(`users.${index}.role_editing`, true)}
                                          aria-label="Set barn-specific access role"
                                          title="Set barn-specific access role"
                                        >
                                          <PencilIconSVG className="BarnSettingsTabV2-inheritedRole-editIcon" />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <SelectField
                                          name={`users.${index}.role_id`}
                                          defaultText="Select Role"
                                          displayOnly={!canUpdateBarn}
                                          itemList={(() => {
                                            const existingUserRoles =
                                              initialSettingsData?.barn?.user_barns
                                                ?.filter((user_barn) => user_barn.user_id === userObj.user_id)
                                                ?.map((user_barn) => user_barn.role_id) || [];
                                            const pendingRolesFromSameUser = values.users
                                              .filter(
                                                (otherUser, otherIndex) =>
                                                  otherIndex !== index &&
                                                  otherUser.user_id === userObj.user_id &&
                                                  otherUser.role_id,
                                              )
                                              .map((otherUser) => otherUser.role_id);
                                            return availableRoles.map((role) => {
                                              const isExistingRole = existingUserRoles.includes(role.id);
                                              const isPendingRole = pendingRolesFromSameUser.includes(role.id);
                                              return {
                                                name: isPendingRole
                                                  ? `${role.name} (already selected for this user)`
                                                  : role.name,
                                                id: role.id,
                                                disabled: isExistingRole || isPendingRole,
                                              };
                                            });
                                          })()}
                                          value={userObj.role_id || ''}
                                          defaultTextDisabled={true}
                                          onChange={(event) => {
                                            // convert empty string back to null for consistency
                                            const newValue = event.target.value === '' ? null : event.target.value;
                                            setFieldValue(`users.${index}.role_id`, newValue);
                                            if (newValue) {
                                              setFieldValue(`users.${index}.role_editing`, true);
                                            }
                                          }}
                                        />
                                        {!userObj.role_id && !userObj.is_tier_only && (
                                          <div className="BarnSettingsTabV2-roleWarning">Please select a role</div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                ) : (
                                  <span>
                                    {userObj.is_tier_only && userObj.inherited_org_role_name
                                      ? `Org: ${userObj.inherited_org_role_name}`
                                      : selectedRole?.name || 'No Role'}
                                  </span>
                                )}
                              </div>
                              <div className="cell">
                                <NotificationTierSelector
                                  selectedTierId={userObj.tier_id}
                                  onTierChange={(tierId) =>
                                    handleUserTierChange(userObj.user_id, tierId, setFieldValue, index)
                                  }
                                  disabled={!canManageUsers || !canUpdateBarn}
                                  label=""
                                  showDescription={false}
                                  planId={currentNotificationPlan?.plan_id}
                                  defaultTierId={resolveDefaultTierId({
                                    orgTierId: userObj.org_tier_id,
                                    planDefaultTierId,
                                  })}
                                  defaultLabel="(Their Default)"
                                />
                              </div>
                              <div className={isNewUser ? 'BarnSettingsTabV2-userRow--new' : ''}>
                                {canManageUsers && (
                                  <Button
                                    content="x"
                                    variant="text"
                                    color="error"
                                    className="remove"
                                    disabled={!canUpdateBarn || !canManageUsers}
                                    onClick={() => {
                                      if (!canUpdateBarn || !canManageUsers) return;
                                      remove(index);
                                    }}
                                  />
                                )}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div >
                    </React.Fragment >
                  )
                  }
                </FieldArray >
              </div >
              <Heading className="TabSection-heading">Calibration Settings</Heading>
              <div className="BarnSettingsTabV2-barnSettings">
                <SelectField
                  label="Calibration Style"
                  name="calibrationStyle"
                  displayOnly={!canUpdateBarn}
                  itemList={CALIBRATION_STYLES.map((s) => {
                    return { ...s };
                  })}
                  onChange={(event) => {
                    const newStyle = event.target.value;
                    if (
                      (newStyle === CalibrationStyle.Continuous ||
                        newStyle === CalibrationStyle.ExactAndContinuous) &&
                      !values.calibrationSchedule
                    ) {
                      setFieldValue('calibrationSchedule', CALIBRATION_SCHEDULES[0].key);
                    }
                  }}
                />
                {(values.calibrationStyle === CalibrationStyle.Continuous ||
                  values.calibrationStyle === CalibrationStyle.ExactAndContinuous) && (
                    <SelectField
                      label="Calibration Schedule"
                      name="calibrationSchedule"
                      displayOnly={!canUpdateBarn}
                      itemList={CALIBRATION_SCHEDULES.map((s) => {
                        return { id: s.key, name: s.name };
                      })}
                    />
                  )}
              </div>
              <Heading className="TabSection-heading">Inventory Management Settings</Heading>
              <div className="BarnSettingsTabV2-barnSettings">
                <NumberInputField
                  name="feedOrderShippingTimeInDays"
                  label={`Feed Mill Minimum Lead Time (Days)`}
                  min={1}
                  max={30}
                  displayOnly={!canUpdateBarn}
                />
                <NumberInputField
                  name="typicalFeedDeliverySizeInTons"
                  label={`Typical Feed Delivery Size (${weightLargeUnitLabel(user?.isMetric)}s)`}
                  min={0.1}
                  max={100}
                  step={0.1}
                  displayOnly={!canUpdateBarn}
                />
              </div>
              <Heading className="TabSection-heading">Automation Settings</Heading>
              <div className="BarnSettingsTabV2-automation">
                <div className="BarnSettingsTabV2-automation-row">
                  <div className="BarnSettingsTabV2-automation-row-label">
                    Automatically mark feed orders as delivered after their scheduled time:
                  </div>
                  <ToggleSwitchField
                    className="BarnSettingsTabV2-automation-row-toggle"
                    name="deliveryAutoComplete"
                    displayOnly={!canUpdateBarn}
                  />
                </div>
                <div className="BarnSettingsTabV2-automation-row">
                  <div className="BarnSettingsTabV2-automation-row-label">
                    Snooze all notifications when the barn is empty:
                  </div>
                  <ToggleSwitchField
                    className="BarnSettingsTabV2-automation-row-toggle"
                    name="animalGroupSnooze"
                    displayOnly={!canUpdateBarn}
                  />
                </div>
              </div>
              <Heading className="TabSection-heading">Features</Heading>
              <div className="BarnSettingsTabV2-feature-section">
                <div className="BarnSettingsTabV2-feature-row">
                  <div className="BarnSettingsTabV2-feature-row-label">Enable Bin Inventory Tracking:</div>
                  <ToggleSwitchField
                    className="BarnSettingsTabV2-automation-row-toggle"
                    name="binInventoryEnabled"
                    displayOnly={!canUpdateBarn}
                  />
                </div>
                <div className="BarnSettingsTabV2-feature-help">
                  <div className="BarnSettingsTabV2-feature-help-card">
                    <div className="BarnSettingsTabV2-feature-help-card-heading">
                      <InfoIcon className="BarnSettingsTabV2-feature-help-icon" />
                      Help
                    </div>
                    <div className="BarnSettingsTabV2-feature-help-content">
                      <strong>Before enabling Bin Inventory Tracking:</strong>
                      <ul>
                        <li>You will need to perform regular bin calibrations to maintain accuracy</li>
                        <li>All feed deliveries must be entered into the system</li>
                        <li>Bin capacities should be configured correctly</li>
                      </ul>
                      <p>
                        Need help getting started? Contact us at{' '}
                        <a href="mailto:support@feedflo.com">support@feedflo.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {
                hasStaffAccess && (
                  <>
                    <Heading className="TabSection-heading">Algorithm Override</Heading>
                    <div className="BarnSettingsTabV2-barnSettings">
                      {algorithmVersionsLoading || barnOverrideLoading ? (
                        <LoadingSkeleton className="BarnSettingsTabV2-skeletonField" />
                      ) : (
                        <>
                          {barnOverrideData?.barn_algorithm_override?.[0] && (
                            <div className="BarnSettingsTabV2-algorithmOverrideInfo">
                              <div>
                                <strong>Current Override:</strong>{' '}
                                {shortenAlgorithmVersion(barnOverrideData.barn_algorithm_override[0].algorithm_version)}
                                {barnOverrideData.barn_algorithm_override[0].algorithm?.description &&
                                  ` - ${barnOverrideData.barn_algorithm_override[0].algorithm.description}`}
                                {barnOverrideData.barn_algorithm_override[0].algorithm?.published_at
                                  ? ' (Published)'
                                  : ' (Unpublished)'}
                              </div>
                            </div>
                          )}
                          <ComboboxField
                            label="Algorithm Version Override"
                            name="algorithmOverrideVersion"
                            disabled={!canUpdateBarn}
                            placeholder="No Override (use default)"
                            items={[
                              { id: '', name: 'No Override (use default)' },
                              ...(algorithmVersionsData?.algorithm?.map((alg) => ({
                                id: alg.version,
                                name: `${shortenAlgorithmVersion(alg.version)}${alg.description ? ` - ${alg.description}` : ''}${alg.published_at ? ' (Published)' : ' (Unpublished)'
                                  }`,
                              })) || []),
                            ]}
                          />
                          {values.algorithmOverrideVersion && values.algorithmOverrideVersion !== '' && (
                            <Button
                              color="error"
                              variant="text"
                              content="Clear Override"
                              onClick={() => {
                                showConfirmationDialog(
                                  'Are you sure you want to clear the algorithm override? The barn will use the default (latest published) algorithm version.',
                                  () => {
                                    setFieldValue('algorithmOverrideVersion', '');
                                  },
                                );
                              }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </>
                )
              }
            </TabSection >
            <TabSection heading="Notification Policy Settings" className="settingsSection">
              <NotificationTierSection
                barnId={barnId}
                organizationId={organizationId}
                formikValues={values}
                setFieldValue={setFieldValue}
                canUpdateBarn={canUpdateBarn}
              />
            </TabSection>
          </Form>
        )}
      </Formik>
    </Page >
  );
}

BarnSettingsTab.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
  barnId: PropTypes.string,
};

export default BarnSettingsTab;
